import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { validateInputAction } from "../../actions/advisor";
import { verifyUserUniqueTokenAction } from "../../actions/auth";
import {
  addCompany,
  getAllCompany,
  getRecommendedPlanAction,
  saveFap2CompanyMyInfoDataAction,
  selectedCompanyStepAction,
  selectPendingCompany,
  setStakeholderMyInfoCallbackUrl,
  submitStakeholderMyInfoResultAction,
} from "../../actions/company";
import { singleFileUploadAction } from "../../actions/files";
import { addOfficerAction, getOfficerList, removeOfficer } from "../../actions/officer";
import { sendPublicSupportMessage } from "../../actions/chatv2";
import {
  addCampaignAiAction,
  getAIObjectivesAction,
  getAiAction,
  getAiFilterAction,
  getLeadsProfileAction,
  getMergedGoalsAction,
  setObjectiveFilter,
  updateObjectiveTitle,
} from "../../actions/peopleai";
import { addShareAction, getShareHolderList, removeShareHolder } from "../../actions/shareHolder";
import { clearMessage, setFailedMessage } from "../../actions/message";
import { comparePlansAction, createBusinessPlanAction, getPlanProductAction, getSingleSubscriptonPlan, selectedPlanProductAction } from "../../actions/subscriptionPlan";
import { manualPaymentAction, setPaymentMethodSelection, subscriptionPlan, updateStripePayableAmountAction } from "../../actions/stripe";
import { ACTIVITY, HOLDERS, backendUrl, country, I_POP_OVER, PREDEFINED_ADDRESS, SUBSCRIPTION_MODULE_TYPE, SUPPORTED_PAYMENT_METHOD } from "../../constants";
import { RoutesPaths } from "../../Router/constant";
import GetToaster from "../../components/header/getToaster";
import RecommendedPlanCard from "../../components/RecommendedPlanCard";
import { companyLink } from "../../components/ContactUs/ContactUs";
import { BankTransferPublicFlow } from "../payment/BankTransferPublic";
import InfoModalStatic from "../dashboard/infoModalStatic";
import ActivateBusinessPlanDetail from "./ActivateBusinessPlanDetail";
import { trackAnalyticsEvent } from "../../firebase";
import "./ActivateBusiness.css";

const BUSINESS_TYPE_OPTIONS = [
  { label: "Wealth retention", value: "wealth_retention" },
  { label: "Trade efficiency", value: "trade_efficiency" },
  { label: "Capital access", value: "capital_access" },
  { label: "Digital assets", value: "digital_assets" },
];

const getTemporaryRandomBusinessType = () =>
  BUSINESS_TYPE_OPTIONS[Math.floor(Math.random() * BUSINESS_TYPE_OPTIONS.length)];

const getRecommendedPlanCategory = recommendedPlan => {
  const planCode = recommendedPlan?.planCode || recommendedPlan?.plan?.code || "";
  const businessType = recommendedPlan?.businessType || recommendedPlan?.plan?.businessType || "";
  const lookupValue = `${planCode} ${businessType}`.toUpperCase();

  if (lookupValue.includes("WEALTH_RETENTION") || lookupValue.includes("WEALTH RETENTION")) {
    return "wealth_retention";
  }

  if (lookupValue.includes("CAPITAL_ACCESS") || lookupValue.includes("CAPITAL ACCESS")) {
    return "capital_access";
  }

  if (lookupValue.includes("DIGITAL_ASSETS") || lookupValue.includes("DIGITAL ASSETS")) {
    return "digital_assets";
  }

  return "trade_efficiency";
};

const getAmountFieldMeta = recommendedPlan => {
  const businessType = getRecommendedPlanCategory(recommendedPlan);

  if (businessType === "wealth_retention") {
    return {
      label: "Assets Under Management (AUM)",
      popover: I_POP_OVER.WEALTH_RETENTION_AMOUNT,
    };
  }

  if (businessType === "capital_access") {
    return {
      label: "Expected Capital Inflow",
      popover: I_POP_OVER.CAPITAL_ACCESS_AMOUNT,
    };
  }

  if (businessType === "digital_assets") {
    return {
      label: "Expected Capital Inflow",
      popover: I_POP_OVER.DIGITAL_ASSETS_AMOUNT,
    };
  }

  return {
    label: "Expected Capital Inflow",
    popover: I_POP_OVER.TRADE_EFFICIENCY_AMOUNT,
  };
};

const steps = [
  "Business identity",
  "Shareholders & directors",
  "Goals & objectives",
  "Business preview",
  "Choose a plan",
  "Review & pay",
];

const TOTAL_STEPS = steps.length;

const activationAnalyticsSteps = {
  1: {
    eventName: "activation_business_identity",
    label: "Business Identity",
  },
  2: {
    eventName: "activation_shareholders_directors",
    label: "Shareholders & Directors",
  },
  3: {
    eventName: "activation_goals_objectives",
    label: "Goals & Objectives",
  },
  4: {
    eventName: "activation_business_preview",
    label: "Business Preview",
  },
  5: {
    eventName: "activation_choose_plan",
    label: "Choose a Plan",
  },
  6: {
    eventName: "activation_payment_page",
    label: "Plan Details (payment page)",
  },
};

const getUserFullName = user =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
  user?.name ||
  user?.fullName ||
  "";

const getActivationUserPayload = user => ({
  app_user_id: getEntityId(user),
  user_name: getUserFullName(user),
});

const getUniqueUser = () => {
  try {
    return JSON.parse(localStorage.getItem("uniqueUser")) || null;
  } catch {
    return null;
  }
};

const getCompanyId = company => company?.id || company?._id;

const getEntityId = entity => {
  if (!entity) return "";
  if (typeof entity === "string") return entity;
  return entity?.id || entity?._id || "";
};

const getCompanyUpdatedScore = (company, index) => {
  const dateValue = company?.updatedAt || company?.updated_at || company?.lastUpdatedAt || company?.createdAt || company?.created_at;
  const timestamp = dateValue ? new Date(dateValue).getTime() : NaN;

  return Number.isNaN(timestamp) ? index : timestamp;
};

const getLatestPendingCompany = pendingCompany => {
  const pendingCompanies = Array.isArray(pendingCompany)
    ? pendingCompany
    : pendingCompany
      ? [pendingCompany]
      : [];

  return pendingCompanies.reduce((latestCompany, company, index) => {
    if (!latestCompany) return company;

    const latestIndex = pendingCompanies.findIndex(item => getCompanyId(item) === getCompanyId(latestCompany));
    const latestScore = getCompanyUpdatedScore(latestCompany, latestIndex);
    const currentScore = getCompanyUpdatedScore(company, index);

    return currentScore >= latestScore ? company : latestCompany;
  }, null);
};

const MYINFO_AUTHORIZE_URL = `${backendUrl}api/myinfo/authorize`;
const STAKEHOLDER_TYPES = [
  { key: "shareholders", title: "Shareholders", singular: "shareholder" },
  { key: "directors", title: "Directors", singular: "director" },
];

const getTypeMeta = type => STAKEHOLDER_TYPES.find(item => item.key === type) || STAKEHOLDER_TYPES[0];
const getDefaultPosition = type => type === "directors" ? "Potential director" : "Potential shareholder";

const getCurrentDateLabel = () => {
  const now = new Date();
  const date = now.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${date} ${time} IST`;
};

const parseCallbackParams = value => {
  const query = String(value || "").includes("?") ? String(value).split("?")[1] : String(value || "");
  const params = new URLSearchParams(query);
  return {
    status: params.get("status"),
    result: params.get("result"),
    id: params.get("myinfoId") || params.get("id") || params.get("state"),
  };
};

const getCleanMyInfoResponse = response => {
  const cleanResponse = JSON.parse(JSON.stringify(response || {}));
  const entityInfo = cleanResponse?.data?.userinfo?.entity_info;

  if (typeof entityInfo === "string") {
    try {
      cleanResponse.data.userinfo.entity_info = JSON.parse(entityInfo);
    } catch (error) {
      console.log("Failed to parse MyInfo entity_info", error);
    }
  }

  return cleanResponse;
};

const getFieldValue = field => field?.value ?? "";

const getMyInfoPersonName = (item, individualKey, entityKey) =>
  getFieldValue(item?.[individualKey]?.name) ||
  getFieldValue(item?.[entityKey]?.name) ||
  getFieldValue(item?.[individualKey]?.person?.name) ||
  getFieldValue(item?.[entityKey]?.entity?.basic_profile?.name) ||
  "";

const getMyInfoPersonId = (item, individualKey, entityKey) =>
  getFieldValue(item?.[individualKey]?.id_number) ||
  getFieldValue(item?.[entityKey]?.registration_number) ||
  getFieldValue(item?.[individualKey]?.person?.uinfin) ||
  getFieldValue(item?.[entityKey]?.entity?.basic_profile?.uen) ||
  getMyInfoPersonName(item, individualKey, entityKey);

const getMyInfoDirectors = appointments => {
  if (!Array.isArray(appointments)) return [];

  return appointments
    .filter(item => String(item?.position?.desc || "").toLowerCase().includes("director"))
    .map((item, index) => {
      const personId =
        getMyInfoPersonId(item, "individual_appointment", "entity_appointment") ||
        getMyInfoPersonId(item, "person_reference", "entity_reference");
      const name =
        getMyInfoPersonName(item, "individual_appointment", "entity_appointment") ||
        getMyInfoPersonName(item, "person_reference", "entity_reference");

      return {
        id: `myinfo-director-${personId || index}`,
        name,
        share_email: "",
        email: "",
        countryCode: "",
        phoneNumber: "",
        position: item?.position?.desc || "Director",
        status: "Official",
        type: "directors",
        dateAdded: getFieldValue(item?.appointment_date) || item?.lastupdated || getCurrentDateLabel(),
        myInfoData: item,
      };
    });
};

const getMyInfoShareholders = shareholders => {
  if (!Array.isArray(shareholders)) return [];

  return shareholders.map((item, index) => {
    const shareType = item?.share_type?.desc;
    const allocation = getFieldValue(item?.allocation);

    const personId =
      getMyInfoPersonId(item, "individual_shareholder", "entity_shareholder") ||
      getMyInfoPersonId(item, "person_shareholder", "entity_shareholder");
    const name =
      getMyInfoPersonName(item, "individual_shareholder", "entity_shareholder") ||
      getMyInfoPersonName(item, "person_shareholder", "entity_shareholder");

    return {
      id: `myinfo-shareholder-${personId || index}`,
      name,
      share_email: "",
      email: "",
      countryCode: "",
      phoneNumber: "",
      position: [shareType, allocation ? `${allocation} shares` : ""].filter(Boolean).join(" - ") || "Shareholder",
      status: "Official",
      type: "shareholders",
      dateAdded: item?.lastupdated || getCurrentDateLabel(),
      myInfoData: item,
    };
  });
};

const getMyInfoStakeholders = response => {
  const cleanResponse = getCleanMyInfoResponse(response);
  const entityInfo = cleanResponse?.data?.userinfo?.entity_info || {};
  return [
    ...getMyInfoShareholders(entityInfo?.shareholders),
    ...getMyInfoDirectors(entityInfo?.appointments),
  ].filter(item => item.name);
};

const getMyInfoCompanyName = response => {
  const cleanResponse = getCleanMyInfoResponse(response);
  return getFieldValue(cleanResponse?.data?.userinfo?.entity_info?.basic_profile?.name);
};

const getApiStakeholderId = item => item?.id || item?._id || item?.userId || item?.shareholderId || item?.officerId;
const getApiStakeholderDate = item => item?.dateAdded || item?.createdAt || item?.updatedAt || getCurrentDateLabel();
const getApiStakeholderName = item => item?.name || item?.fullName || item?.user?.name || [item?.first_name, item?.last_name].filter(Boolean).join(" ");
const getApiStakeholderEmail = item => item?.share_email || item?.email || item?.user?.email || "";
const normalizeCompareValue = value => String(value || "").trim().toLowerCase();
const getStakeholderCompareKey = item => `${item?.type || ""}-${normalizeCompareValue(item?.email || item?.share_email || item?.name)}`;

const mapApiStakeholder = (item, type) => ({
  id: getApiStakeholderId(item),
  name: getApiStakeholderName(item),
  share_email: getApiStakeholderEmail(item),
  email: getApiStakeholderEmail(item),
  countryCode: item?.countryCode || "",
  phoneNumber: item?.phoneNumber || "",
  position: item?.positionName || item?.position || getDefaultPosition(type),
  status: item?.statusName || item?.status || "Invite sent",
  type,
  dateAdded: getApiStakeholderDate(item),
  raw: item,
});

const buildStakeholderPayload = (stakeholder, type, companyId) => ({
  name: stakeholder?.name || "",
  share_email: stakeholder?.share_email || stakeholder?.email || "",
  phoneNumber: stakeholder?.phoneNumber || "",
  countryCode: stakeholder?.countryCode?.dial_code || stakeholder?.countryCode || "",
  company: companyId,
  position: type === "directors" ? HOLDERS.directors : HOLDERS.shareHolders,
  type: type === "directors" ? HOLDERS.directors : HOLDERS.shareHolders,
  global: 0,
  skipInviteForCompanyCreation: true,
});

const goalOptions = [
  {
    key: "stakeholders",
    title: "Find more stakeholders",
    description: "Connect with the right stakeholders, expand your network, and unlock new growth opportunities for your business.",
    icon: "network",
    iconUrl: "/assets/img/goal-stakeholders.png",
  },
  {
    key: "tax",
    title: "Be more tax-efficient",
    description: "Improve your structure and planning so the business can operate with better tax awareness.",
    icon: "tax",
    iconUrl: "/assets/img/goal-tax.png",
  },
  {
    key: "risk",
    title: "Strengthen compliance & reduce risk",
    description: "Build a stronger operating foundation with clearer controls, reporting, and risk management.",
    icon: "lock",
    iconUrl: "/assets/img/goal-risk.png",
  },
  {
    key: "cash",
    title: "Improve cash flow & financial health",
    description: "Create a more resilient finance model with better visibility and working-capital discipline.",
    icon: "cash",
    iconUrl: "/assets/img/goal-cash.png",
  },
  {
    key: "brand",
    title: "Grow brand presence & credibility",
    description: "Increase market confidence and make your business easier for partners to understand.",
    icon: "growth",
    iconUrl: "/assets/img/goal-brand.png",
  },
];

const objectiveOptions = [
  "Stakeholder Engagement",
  "Network Expansion",
  "Partnership Development",
  "Community Connections",
  "Local Sourcing Initiatives",
  "Fresh Produce Outreach",
  "Quality Assurance Programs",
  "Growth Opportunity Mapping",
];

const audienceOptions = {
  roles: ["Community Manager", "Stakeholder Engagement Lead", "Partnership Development Manager", "Local Sourcing Coordinator"],
  seniority: ["Manager", "Senior Manager", "Director"],
  locations: ["Singapore", "India", "Malaysia", "Indonesia"],
  industries: ["Agriculture", "Food Production", "Community Development"],
};

const staticStepContent = {
  2: {
    title: "Shareholders & Directors",
    subtitle: "Add ownership and appointment details for the company.",
    items: ["Shareholder information", "Director appointments", "Contact details"],
  },
  3: {
    title: "Goals & Objectives",
    subtitle: "Capture what the business is trying to achieve.",
    items: ["Business purpose", "Commercial goals", "Target outcomes"],
  },
  4: {
    title: "Business Classification",
    subtitle: "Confirm activity and classification information.",
    items: ["Primary classification", "Secondary activity", "Licence status"],
  },
  6: {
    title: "Review & Pay",
    subtitle: "Review the activation package before payment.",
    items: ["Business summary", "Plan summary", "Payment method"],
  },
};

const stepIntroContent = {
  1: {
    title: "Start a holding company",
    subtitle: "Are you advising or building a commercial venture for yourself, clients, family or friends? If so, please create a new company or register your existing company with us.",
  },
  2: {
    title: "Shareholders & Directors",
    subtitle: "Add the people involved in your business. You can do this now or skip and come back later.",
  },
  3: {
    title: "Tell us what you want to do",
    subtitle: "Choose what you're building, set your main objective and who you want to reach with your campaign.",
  },
  4: {
    title: "Business preview",
    subtitle: "Review the details you added before choosing your business plan.",
  },
  5: {
    title: "Choose a Plan",
    subtitle: "Select the plan type that best fits your needs. You can change later.",
  },
  6: {
    title: "Review & pay",
    subtitle: "Review your quotation, add payer details, choose a payment method, and complete payment.",
  },
};

const stripHtml = value =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const capitalizePlanName = value =>
  String(value || "Business plan")
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

const getPlanDescription = plan =>
  stripHtml(plan?.title);

const getPlanBullets = plan => {
  const description = getPlanDescription(plan);
  const bullets = description
    .split(/(?:\s+[\u2022]\s*|\n+)/)
    .map(item => item.trim())
    .filter(Boolean)
    .slice(0, 4);

  return bullets.length > 0 ? bullets : ["Select this plan to continue."];
};

const hasHtmlContent = value =>
  !!String(value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const normalizeHtmlLink = href => {
  if (!href) return null;
  if (/^https?:\/\//i.test(href)) return href;
  if (href.startsWith("//")) return `https:${href}`;
  if (href.startsWith("/")) return `https://www.kimbocorp.com${href}`;
  return `https://${href}`;
};

const appendInlineStyle = (attributes, styleToAppend) => {
  if (/\sstyle\s*=/i.test(attributes)) {
    return attributes.replace(
      /\sstyle\s*=\s*(["'])(.*?)\1/i,
      (_match, quote, styleValue) => ` style=${quote}${styleValue}; ${styleToAppend}${quote}`,
    );
  }

  return `${attributes} style="${styleToAppend}"`;
};

const getHighlightStyle = attributes => {
  const value = String(attributes || "").toLowerCase();
  const isSuccess =
    value.includes("success") ||
    value.includes("guarantee") ||
    value.includes("refund") ||
    value.includes("green") ||
    value.includes("#eaf7ee") ||
    value.includes("#2f9e44") ||
    value.includes("#22543d") ||
    value.includes("#ecfdf3") ||
    value.includes("#16a34a");
  const isWarning =
    value.includes("warning") ||
    value.includes("highlight") ||
    value.includes("amber") ||
    value.includes("yellow") ||
    value.includes("notice") ||
    value.includes("#fff4df") ||
    value.includes("#d99a21") ||
    value.includes("#5f4a1a") ||
    value.includes("#fef3c7") ||
    value.includes("#fff0c2");

  if (isSuccess) {
    return "background-color: #eaf7ee; border-left: 4px solid #2f9e44; border-radius: 6px; padding: 10px 14px; margin-top: 12px; margin-bottom: 12px; color: #22543d;";
  }

  if (isWarning) {
    return "background-color: #fff4df; border-left: 4px solid #d99a21; border-radius: 6px; padding: 10px 14px; margin-top: 12px; margin-bottom: 12px; color: #5f4a1a;";
  }

  return "";
};

const sanitizeHtml = value =>
  String(value || "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");

const prepareDeclarationHtml = html =>
  sanitizeHtml(html).replace(/<(div|p|section|article|blockquote)([^>]*)>/gi, (match, tag, attributes) => {
    const highlightStyle = getHighlightStyle(attributes);
    if (!highlightStyle) return match;

    return `<${tag}${appendInlineStyle(attributes, highlightStyle)}>`;
  });

const getPlanContent = (plan, key) =>
  plan?.[key] ||
  plan?.user?.[key] ||
  plan?.plan?.[key] ||
  plan?.subscriptionPlan?.[key] ||
  "";

const getRecommendedPlanContent = plan =>
  [
    plan?.whoQualify,
    plan?.recommendedPlan?.whoQualify,
    plan?.plan?.whoQualify,
    plan?.exampleContent,
    plan?.recommendedPlan?.exampleContent,
    plan?.plan?.exampleContent,
    plan?.reclassification,
    plan?.recommendedPlan?.reclassification,
    plan?.plan?.reclassification,
  ].filter(hasHtmlContent).join("");

const getPlanRouteParam = plan => (typeof plan === "string" ? plan : plan?.slug || plan?.id);

const getPlanPrice = plan => plan?.price ?? plan?.amount ?? plan?.payableAmount ?? plan?.basePrice ?? null;

const BUSINESS_PLAN_DISPLAY_MODE = {
  FEASIBILITY: "FEASIBILITY",
  FIXED: "FIXED",
  QUOTATION: "QUOTATION",
};

const BUSINESS_PLAN_PRICE_MAP = {
  WEALTH_RETENTION_UNLICENSED: { feasibility: 1425, activation: 9500 },
  TRADE_EFFICIENCY_UNLICENSED: { feasibility: 375, activation: 2500 },
  TRADE_EFFICIENCY_LICENSED: { feasibility: 975, activation: 6500 },
  CAPITAL_ACCESS_UNLICENSED: { feasibility: 750, activation: 5000 },
  DIGITAL_ASSETS_UNLICENSED: { feasibility: 825, activation: 5500 },
  WEALTH_RETENTION_LICENSED_SFO: { fixed: 37000 },
  WEALTH_RETENTION_LICENSED_MFO: { quoteFrom: 65000 },
  CAPITAL_ACCESS_LICENSED: { quoteFrom: 58000 },
  DIGITAL_ASSETS_LICENSED: { quoteFrom: 68000 },
};

const FEASIBILITY_PLAN_CODES = [
  "WEALTH_RETENTION_UNLICENSED",
  "TRADE_EFFICIENCY_UNLICENSED",
  "TRADE_EFFICIENCY_LICENSED",
  "CAPITAL_ACCESS_UNLICENSED",
  "DIGITAL_ASSETS_UNLICENSED",
];

const QUOTATION_OPTION_CODES = ["PLAN_A_QUOTATION", "PLAN_B_QUOTATION"];

const compareEnabledPlanCodes = [
  "WEALTH_RETENTION_UNLICENSED",
  "TRADE_EFFICIENCY_UNLICENSED",
  "TRADE_EFFICIENCY_LICENSED",
  "CAPITAL_ACCESS_UNLICENSED",
  "DIGITAL_ASSETS_UNLICENSED",
];

const normalizePlanText = value =>
  String(value || "")
    .toLowerCase()
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

const getPlanText = plan =>
  normalizePlanText([
    plan?.name,
    plan?.title,
    plan?.fullName,
    plan?.businessPlanPayload?.plan?.fullName,
    plan?.businessPlanPayload?.plan?.name,
    plan?.businessPlanOptionCode,
    plan?.businessPlanOption,
  ].filter(Boolean).join(" "));

const getPlanActivationYearTotal = plan => {
  const selectedOption = plan?.businessPlanOption;

  return (
    plan?.activationYearTotal ??
    (selectedOption ? plan?.businessPlanPayload?.[selectedOption]?.activationYearTotal : null) ??
    plan?.businessPlanPayload?.planA?.activationYearTotal ??
    plan?.businessPlanPayload?.planB?.activationYearTotal ??
    null
  );
};

const getBusinessPlanCode = plan =>
  plan?.businessPlanCode ?? plan?.businessPlanPayload?.plan?.code ?? plan?.planCode ?? null;

const getBusinessPlanPriceKey = plan => {
  const code = getBusinessPlanCode(plan);
  const text = getPlanText(plan);

  if (code === "WEALTH_RETENTION_LICENSED") {
    return text.includes("mfo") ? "WEALTH_RETENTION_LICENSED_MFO" : "WEALTH_RETENTION_LICENSED_SFO";
  }

  return code;
};

const getBusinessPlanDisplayMode = plan => {
  const priceKey = getBusinessPlanPriceKey(plan);
  const text = getPlanText(plan);

  if (
    QUOTATION_OPTION_CODES.includes(plan?.businessPlanOptionCode) ||
    QUOTATION_OPTION_CODES.includes(plan?.businessPlanOption)
  ) {
    return BUSINESS_PLAN_DISPLAY_MODE.QUOTATION;
  }

  if (["WEALTH_RETENTION_LICENSED_MFO", "CAPITAL_ACCESS_LICENSED", "DIGITAL_ASSETS_LICENSED"].includes(priceKey)) {
    return BUSINESS_PLAN_DISPLAY_MODE.QUOTATION;
  }

  if (priceKey === "WEALTH_RETENTION_LICENSED_SFO") {
    return text.includes("fixed") || plan?.businessPlanOption === "planB"
      ? BUSINESS_PLAN_DISPLAY_MODE.FIXED
      : BUSINESS_PLAN_DISPLAY_MODE.QUOTATION;
  }

  if (FEASIBILITY_PLAN_CODES.includes(priceKey)) {
    return BUSINESS_PLAN_DISPLAY_MODE.FEASIBILITY;
  }

  return parseFloat(getPlanPrice(plan) || 0) === 0
    ? BUSINESS_PLAN_DISPLAY_MODE.QUOTATION
    : BUSINESS_PLAN_DISPLAY_MODE.FEASIBILITY;
};

const isActivationBusinessPlanCard = plan => {
  const text = getPlanText(plan);

  return (
    text.includes("activation") ||
    text.includes("platform fee") ||
    plan?.businessPlanOption === "planA" ||
    plan?.businessPlanOptionCode === "PLAN_A_STANDARD"
  ) && !text.includes("feasibility");
};

const isTradeEfficiencyActivationPlan = plan => {
  const code = getBusinessPlanCode(plan);
  const text = getPlanText(plan);

  return (
    isActivationBusinessPlanCard(plan) &&
    (
      code === "TRADE_EFFICIENCY_UNLICENSED" ||
      (
        !code &&
        text.includes("trade efficiency") &&
        text.includes("unlicensed")
      )
    )
  );
};

const getBusinessPlanCardTitle = plan => {
  const mode = getBusinessPlanDisplayMode(plan);

  if (mode === BUSINESS_PLAN_DISPLAY_MODE.QUOTATION) {
    return "Request a quote";
  }

  if (mode === BUSINESS_PLAN_DISPLAY_MODE.FIXED) {
    return "Fixed annual";
  }

  return isActivationBusinessPlanCard(plan) ? "Activation" : "Feasibility";
};

const getBusinessPlanQuoteFromPrice = plan => {
  const priceKey = getBusinessPlanPriceKey(plan);
  return BUSINESS_PLAN_PRICE_MAP[priceKey]?.quoteFrom || null;
};

const formatPlanAmount = value => {
  const amount = Number(value || 0);
  return Number.isFinite(amount)
    ? amount.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : "0";
};

const getPlanCurrencyCode = plan => String(plan?.currency?.value || plan?.currency || "SGD").toUpperCase();

const getBusinessPlanCardPrice = plan => {
  const priceKey = getBusinessPlanPriceKey(plan);
  const priceConfig = BUSINESS_PLAN_PRICE_MAP[priceKey] || {};
  const mode = getBusinessPlanDisplayMode(plan);
  const currency = getPlanCurrencyCode(plan);

  if (mode === BUSINESS_PLAN_DISPLAY_MODE.QUOTATION) {
    const planPrice = Number(getPlanPrice(plan) || getPlanActivationYearTotal(plan) || 0);
    if (priceConfig.quoteFrom && planPrice <= 0) {
      return "Request a quote";
    }

    return planPrice > 0
      ? `${currency} ${formatPlanAmount(planPrice)}`
      : "Request a quote";
  }

  if (mode === BUSINESS_PLAN_DISPLAY_MODE.FIXED) {
    return `${currency} ${formatPlanAmount(getPlanActivationYearTotal(plan) || priceConfig.fixed || 37000)}/yr, no AUM ceiling`;
  }

  if (isActivationBusinessPlanCard(plan)) {
    const activationPrice = Number(getPlanActivationYearTotal(plan) || getPlanPrice(plan) || 0);
    return activationPrice > 0
      ? `${currency} ${formatPlanAmount(activationPrice)}`
      : 'Request a quote';
  }

  const planPrice = Number(getPlanPrice(plan) || 0);
  return planPrice > 0 ? `${currency} ${formatPlanAmount(planPrice)}` : "Request a quote";
};

const isBusinessPlan = plan =>
  parseInt(plan?.planType?.value) === SUBSCRIPTION_MODULE_TYPE.CUSTOM && plan?.activationplan === true;

const PLAN_CARD_IMAGES = [
  "/assets/img/rocket-plan.png",
  "/assets/img/quote-plan.png",
  "/assets/img/planBack.png",
  "/assets/img/estatePlan.png",
];

const getColumnKey = column =>
  String(column || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const shouldShowPlatformFeeInfo = (row, column) =>
  getColumnKey(column) === "activation" &&
  ["access duration", "renewal"].includes(String(row?.label || "").trim().toLowerCase());

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "RS";
  return parts.slice(0, 2).map(part => part[0]).join("").toUpperCase();
};

const normalizeWebsite = value => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
};

const getDisplayWebsite = value => (value || "").replace(/^https?:\/\//i, "");

function StepRail({ selectedStep }) {
  return (
    <aside className="activate-sidebar">
      {/* <div className="activate-brand">corp</div> */}
      <img
        src="assets/img/inproved-logo.svg"
        className="inproved-logo cursorPointer mb-4"
        alt="kimbocorp"
      />
      <div className="activate-step-list">
        {steps.map((label, index) => {
          const step = index + 1;
          const isComplete = step < selectedStep;
          const isActive = step === selectedStep;

          return (
            <div className={`activate-step ${isActive ? "active" : ""} ${isComplete ? "complete" : ""}`} key={label}>
              <div className="activate-step-marker">
                {isComplete ? <span className="activate-step-check" aria-hidden="true" /> : step}
              </div>
              <div>
                <p>{label}</p>
                <span>{isComplete ? "Completed" : isActive ? "In progress" : "Not started"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function ActivationHeader({ user }) {
  const name = `${user?.first_name || "Ramneek"} ${user?.last_name || "Singh"}`.trim();
  const email = user?.email || "ramneek.singh@email.com";

  return (
    <header className="activate-header">
      <div className="activate-heading">
        <h1>Activate your business</h1>
      </div>
      <div className="activate-user">
        <div className="activate-avatar">{getInitials(name)}</div>
        <div>
          <strong>{name}</strong>
          <p>{email}</p>
        </div>
      </div>
    </header>
  );
}

function StepIntroCard({ step, subStep, title, subtitle }) {
  const content = stepIntroContent[step];

  if (!content) return null;

  const stepLabel = subStep ? `Step ${step}.${subStep}` : `Step ${step}`;
  const displayTitle = title || content.title;
  const displaySubtitle = subtitle || content.subtitle;

  return (
    <div className="activate-substep-card activate-step-card">
      <div>
        <p><strong>{stepLabel}: </strong> {displayTitle}</p>
        <span>{displaySubtitle}</span>
      </div>
    </div>
  );
}

function PreviewCard({ data, onEdit }) {
  const getPreviewValue = value => {
    if (typeof value !== "string") return value;
    return value.trim();
  };
  const renderPreviewValue = value => {
    const previewValue = getPreviewValue(value);

    if (!previewValue) {
      return <span className="activate-preview-placeholder" aria-label="Not filled" />;
    }

    return previewValue;
  };

  return (
    <section className="activate-card activate-preview">
      <h2>Your Selection Preview</h2>
      <p className="activate-muted">Review your information from the previous step</p>
      <div className="activate-divider" />
      <div className="activate-preview-title">
        <span className="activate-small-icon">ID</span>
        <strong>Business Identity</strong>
        <button type="button" onClick={onEdit}>Edit</button>
      </div>
      <dl>
        <dt>Business Name</dt>
        <dd>{renderPreviewValue(data.companyName)}</dd>
        <dt>Primary Activity</dt>
        <dd>{renderPreviewValue(data.primaryActivity)}</dd>
        <dt>Secondary Activity</dt>
        <dd>{renderPreviewValue(data.secondaryActivity)}</dd>
        <dt>Website</dt>
        <dd>{renderPreviewValue(data.companyWebsite ? getDisplayWebsite(data.companyWebsite) : "")}</dd>
      </dl>
      <div className="activate-description-box">
        <strong>Business Description</strong>
        <p>{renderPreviewValue(data.about || data.aboutPrompt)}</p>
      </div>
    </section>
  );
}

function PlanArt({ type }) {
  return (
    <div className={`activate-plan-art ${type}`}>
      <div className="art-sky" />
      <div className="art-base" />
      {type === "rocket" && <div className="art-rocket"><span /></div>}
      {type === "bank" && (
        <>
          <div className="art-bank-roof" />
          <div className="art-bank-body" />
          <div className="art-shield">OK</div>
        </>
      )}
      {type === "quote" && (
        <>
          <div className="art-clipboard">QUOTE</div>
          <div className="art-coin">$</div>
        </>
      )}
    </div>
  );
}

function CompareIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 21h12M12 3v18M5 7h14M7 7l-3 7h6L7 7Zm10 0-3 7h6l-3-7Z" />
    </svg>
  );
}

function PlanCheckIcon() {
  return (
    <svg viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <circle cx="9" cy="9" r="7" />
      <path d="M5.6 9.2 8 11.6l4.8-4.7" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <circle cx="9" cy="9" r="6.75" />
      <path d="M9 8.25V12M9 5.6v.4" />
    </svg>
  );
}

function PlatformFeeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2.5 5h11M3.3 3.3h9.4c.6 0 1 .4 1 1v7.4c0 .6-.4 1-1 1H3.3c-.6 0-1-.4-1-1V4.3c0-.6.4-1 1-1ZM4.3 10.2h3.1" stroke={"#2977B5"} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FeasibilityIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M5.2 2.5h5.6M4 5.2h8M4 8h8M4 10.8h5.2M3 1.5h10v13H3v-13Z" stroke={"#2977B5"} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BankingIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2.2 6.4h11.6M3 13.2h10M4 6.4v6.4M6.7 6.4v6.4M9.3 6.4v6.4M12 6.4v6.4M8 2.2l5.4 2.8H2.6L8 2.2Z" stroke={'#2977B5'} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DeclarationHtml({ html, className = "" }) {
  const preparedHtml = useMemo(() => prepareDeclarationHtml(html), [html]);

  const handleClick = event => {
    const link = event.target.closest("a");
    if (!link) return;

    const url = normalizeHtmlLink(link.getAttribute("href"));
    if (!url) return;

    event.preventDefault();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!hasHtmlContent(preparedHtml)) return null;

  return (
    <div
      className={`activate-declaration-html ${className}`.trim()}
      onClick={handleClick}
      dangerouslySetInnerHTML={{ __html: preparedHtml }}
    />
  );
}

function QualificationCheckIcon() {
  return (
    <span className="activate-qualification-check" aria-hidden="true">
      <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
        <path d="M3 7.2L5.7 9.8L11 4.2" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function PlanQualificationCard({ content }) {
  if (!hasHtmlContent(content)) return null;

  return (
    <section className="activate-plan-qualification">
      <QualificationCheckIcon />
      <DeclarationHtml html={content} />
    </section>
  );
}

function TermsCheckIcon() {
  return (
    <svg viewBox="0 0 14 14" aria-hidden="true" focusable="false">
      <path d="M3 7.2 5.7 9.8 11 4.2" />
    </svg>
  );
}

function TermsContentCard({ icon, title, content }) {
  if (!hasHtmlContent(content)) return null;

  return (
    <section className="activate-terms-card">
      <div className="activate-terms-card-title">
        {icon}
        <h3>{title}</h3>
      </div>
      <DeclarationHtml html={content} />
    </section>
  );
}

function TermsConfirmationStep({ plan, onBack, onContinue }) {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const isFeasibilityPlan =
    getPlanText(plan).includes("feasibility") ||
    !!getPlanContent(plan, "feasibltyContent") ||
    !isActivationBusinessPlanCard(plan);
  const feasibilityContent =
    getPlanContent(plan, "feasibltyContent") ||
    getPlanContent(plan, "feasibilityContent");
  const platformFeeContent = getPlanContent(plan, "platformFeeContent");
  const bankingContent = getPlanContent(plan, "bankingContent");
  const confirmationLabel = isFeasibilityPlan
    ? "I understand this is non-refundable and does not form a company."
    : "I have read and understand the Platform Fee and banking terms above.";

  return (
    <>
      <StepIntroCard
        step={5}
        subStep={1}
        title="Confirm your terms"
        subtitle={isFeasibilityPlan
          ? "Before you see the final amount, please read what you're actually getting with Feasibility."
          : "Before you see the final amount, please read what your fees cover and how banking works."}
      />

      <div className="activate-terms-list">
        {isFeasibilityPlan ? (
          <TermsContentCard
            icon={<FeasibilityIcon />}
            title="What Feasibility is - and isn't"
            content={feasibilityContent}
          />
        ) : (
          <>
            <TermsContentCard
              icon={<PlatformFeeIcon />}
              title="Why there's a Platform Fee"
              content={platformFeeContent}
            />
            <TermsContentCard
              icon={<BankingIcon />}
              title="How your banking works"
              content={bankingContent}
            />
          </>
        )}
        {!hasHtmlContent(feasibilityContent) && !hasHtmlContent(platformFeeContent) && !hasHtmlContent(bankingContent) && (
          <div className="activate-empty-state">No terms found for this plan.</div>
        )}
      </div>

      <button
        type="button"
        className={`activate-terms-confirmation ${termsAccepted ? "checked" : ""}`}
        onClick={() => setTermsAccepted(current => !current)}
      >
        <span>{termsAccepted && <TermsCheckIcon />}</span>
        <strong>{confirmationLabel}</strong>
      </button>

      <div className="activate-actions">
        <button type="button" className="activate-back" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="activate-continue"
          onClick={onContinue}
          disabled={!termsAccepted}
        >
          Continue
        </button>
      </div>
    </>
  );
}

function ComparisonHeaderIcon({ type }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {type === 0 ? (
        <>
          <path d="M5 19c3.5-.5 6-3 7-6.5M12 12.5C15 11.5 18 8 19 4c-4 1-7.5 4-8.5 7M12 12.5l3 3M10.5 11l-2-2M10.5 11 6 15.5M15 15.5 13 20l-2.5-2.5M8.5 9 4 11l2.5 2.5" />
        </>
      ) : type === 1 ? (
        <>
          <path d="M4 20h16M6 20V8h6v12M12 20V4h6v16" />
          <path d="M8.5 11h1M8.5 14h1M8.5 17h1M14.5 7h1M14.5 10h1M14.5 13h1M14.5 16h1" />
        </>
      ) : (
        <>
          <path d="M8 5H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M8 5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2M8 5h8M8 11h8M8 15h6" />
        </>
      )}
    </svg>
  );
}

function PlanComparisonModal({ onClose, planName }) {
  const dispatch = useDispatch();
  const { planCompare, planCompareLoader } = useSelector(state => state.subscriptionPlanProducts || {});
  const [infoModalShow, setInfoModalShow] = useState(false);
  const columns = planCompare?.columns || [];
  const rows = planCompare?.rows || [];
  const comparisonGridStyle = columns.length > 0
    ? { gridTemplateColumns: `minmax(150px, 0.86fr) repeat(${columns.length}, minmax(180px, 1fr))` }
    : undefined;

  useEffect(() => {
    if (planName) {
      dispatch(comparePlansAction({ name: planName }));
    }
  }, [dispatch, planName]);

  return (
    <div className="activate-modal-backdrop activate-comparison-backdrop" role="presentation" onClick={onClose}>
      <section
        className="activate-comparison-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="planComparisonTitle"
        onClick={event => event.stopPropagation()}
      >
        <div className="activate-comparison-header">
          <div>
            <h2 id="planComparisonTitle">Plan comparison</h2>
            {planCompare?.planName ? <strong>{planCompare.planName}</strong> : null}
            <p>Compare the options to find the best fit for your needs.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close plan comparison">
            Close
          </button>
        </div>

        <div className="activate-comparison-body">
          {planCompareLoader ? (
            <div className="activate-comparison-loading">
              <span />
            </div>
          ) : columns.length > 0 && rows.length > 0 ? (
            <div className="activate-comparison-table" style={comparisonGridStyle} role="table" aria-label="Plan comparison">
              <div className="activate-comparison-row activate-comparison-table-head" role="row">
                <div role="columnheader" />
                {columns.map((column, index) => (
                  <div
                    role="columnheader"
                    key={`${column}-${index}`}
                    className={index === columns.length - 1 ? "activate-comparison-cell-last" : ""}
                  >
                    <span className="activate-comparison-plan-icon" aria-hidden="true">
                      <ComparisonHeaderIcon type={index} />
                    </span>
                    <strong>{column}</strong>
                  </div>
                ))}
              </div>

              {rows.map((row, rowIndex) => (
                <div className="activate-comparison-row" role="row" key={`${row.label}-${rowIndex}`}>
                  <div role="rowheader">{row.label}</div>
                  {columns.map((column, columnIndex) => {
                    const value = row[getColumnKey(column)] || "-";
                    const showInfo = shouldShowPlatformFeeInfo(row, column);

                    return (
                      <div
                        role="cell"
                        key={`${row.label}-${column}`}
                        className={columnIndex === columns.length - 1 ? "activate-comparison-cell-last" : ""}
                      >
                        <span className="activate-comparison-cell-value">{value}</span>
                        {showInfo && (
                          <button
                            type="button"
                            className="activate-comparison-info"
                            onMouseEnter={() => setInfoModalShow(true)}
                            onClick={() => setInfoModalShow(true)}
                            aria-label="Platform Fee - why this is required"
                          >
                            <img
                              src="assets/img/info-icon.svg"
                              alt=""
                              className="m-0 pb-1 cursorPointer"
                            />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : (
            <div className="activate-comparison-empty">No comparison data found.</div>
          )}
        </div>
        <InfoModalStatic
          infoModalStaticShow={infoModalShow}
          infoModalStaticClose={() => setInfoModalShow(false)}
          infoModalStaicShowType={I_POP_OVER.PLATFORM_FEE_REQUIRED}
        />
      </section>
    </div>
  );
}

function PlanOptionCard({ plan, recommended, selected, onSelect, onLearnMore }) {
  const displayMode = getBusinessPlanDisplayMode(plan);
  const planTitle = getBusinessPlanCardTitle(plan);
  const price = getBusinessPlanCardPrice(plan);
  const quoteFromPrice = displayMode === BUSINESS_PLAN_DISPLAY_MODE.QUOTATION
    ? getBusinessPlanQuoteFromPrice(plan)
    : null;
  const isLimitedTimePlan = isTradeEfficiencyActivationPlan(plan);

  return (
    <button
      type="button"
      className={`activate-plan ${selected ? "selected" : ""}`}
      onClick={onSelect}
    >
      {(recommended || isLimitedTimePlan) && (
        <span className="activate-plan-badges">
          {recommended && <span className="activate-badge">Recommended</span>}
          {isLimitedTimePlan && <span className="activate-badge activate-badge-danger">LIMITED TIME</span>}
        </span>
      )}
      <div className="activate-plan-copy">
        <h3>{capitalizePlanName(plan?.name)}</h3>
        {displayMode !== BUSINESS_PLAN_DISPLAY_MODE.QUOTATION && (
          <p className="activate-plan-card-title">{planTitle}</p>
        )}
        <div className="activate-plan-bullets">
          {getPlanBullets(plan).map(item => (
            <div key={item} className="activate-plan-bullet">
              <PlanCheckIcon />
              <span>{item}</span>
            </div>
          ))}
        </div>
        {quoteFromPrice ? (
          <strong>From {getPlanCurrencyCode(plan)} {formatPlanAmount(quoteFromPrice)}</strong>
        ) : (
          price && <strong>{price}</strong>
        )}
        {isLimitedTimePlan && (
          <em className="activate-plan-limited-note">This rate is available for a limited time only</em>
        )}
        <span
          className="activate-plan-learn"
          onClick={event => {
            event.stopPropagation();
            onLearnMore?.();
          }}
        >
          Learn more
        </span>
      </div>
      <div className="activate-radio">{selected && <span />}</div>
    </button>
  );
}

function PlansStep({ companyId, onBack, onContinue, selectedPlan, setSelectedPlan, navigationDisabled = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const createdBusinessPlanKeyRef = useRef(null);
  const [continueLoader, setContinueLoader] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [detailPlan, setDetailPlan] = useState(null);
  const [termsPlan, setTermsPlan] = useState(null);
  const {
    loadProductPlanList,
    productPlanList = [],
    selectedPlanProduct,
    selectedPlanProductCurrentPage,
  } = useSelector(state => state.subscriptionPlanProducts || {});
  const { paymentMethodSelection, stripeType } = useSelector(state => state.stripe || {});
  const { advisorCompany, pendingCompanySelected, recommendedPlan, recommendedPlanPricing, selectedCompany } = useSelector(state => state.company || {});
  const { user: profileUser } = useSelector(state => state.user || {});
  const selectedPlanProductCurrentPageRef = useRef(selectedPlanProductCurrentPage);

  const availableBusinessPlans = useMemo(
    () => productPlanList.filter(isBusinessPlan),
    [productPlanList]
  );

  const visibleBusinessPlans = useMemo(() => {
    const seenSinglePathPlans = new Set();

    return availableBusinessPlans.filter(plan => {
      const priceKey = getBusinessPlanPriceKey(plan);
      const mode = getBusinessPlanDisplayMode(plan);

      if (mode === BUSINESS_PLAN_DISPLAY_MODE.QUOTATION) {
        const planOptionKey =
          plan?.businessPlanOptionCode ||
          plan?.businessPlanOption ||
          plan?.id ||
          plan?._id ||
          "single";
        const singlePathKey = `${priceKey}-${mode}-${planOptionKey}`;
        if (seenSinglePathPlans.has(singlePathKey)) {
          return false;
        }

        seenSinglePathPlans.add(singlePathKey);
      }

      return true;
    });
  }, [availableBusinessPlans]);

  const recommendedBusinessPlan = useMemo(() => {
    const secondPlan = visibleBusinessPlans[1];

    if (secondPlan) {
      return secondPlan;
    }

    return visibleBusinessPlans.find(plan =>
      getBusinessPlanDisplayMode(plan) !== BUSINESS_PLAN_DISPLAY_MODE.QUOTATION
    ) || visibleBusinessPlans[0] || null;
  }, [visibleBusinessPlans]);

  const activePlan = useMemo(() => {
    const selectedProductPlan = visibleBusinessPlans.find(
      plan => getPlanRouteParam(plan) === getPlanRouteParam(selectedPlanProduct)
    );
    const recommendedSelectedPlan = visibleBusinessPlans.find(
      plan => getPlanRouteParam(plan) === getPlanRouteParam(recommendedBusinessPlan)
    );

    return (
      visibleBusinessPlans.find(plan => getPlanRouteParam(plan) === selectedPlan) ||
      recommendedSelectedPlan ||
      (isBusinessPlan(selectedPlanProduct) ? selectedProductPlan : null) ||
      visibleBusinessPlans[0] ||
      null
    );
  }, [recommendedBusinessPlan, selectedPlan, selectedPlanProduct, visibleBusinessPlans]);
  const shouldShowComparePlans =
    visibleBusinessPlans.length > 1 && compareEnabledPlanCodes.includes(getBusinessPlanCode(activePlan));
  const shouldReservePlanBadgeSpace =
    !shouldShowComparePlans &&
    getBusinessPlanDisplayMode(visibleBusinessPlans[0]) !== BUSINESS_PLAN_DISPLAY_MODE.QUOTATION;
  const selectedPlanDisplayMode = getBusinessPlanDisplayMode(activePlan);
  const isRequestQuotePlan = selectedPlanDisplayMode === BUSINESS_PLAN_DISPLAY_MODE.QUOTATION;
  const activeCompany =
    [pendingCompanySelected, selectedCompany, advisorCompany].find(company => getCompanyId(company) === companyId) ||
    pendingCompanySelected ||
    selectedCompany ||
    advisorCompany ||
    { id: companyId, companyName: "this company" };
  const uniqueUser = useMemo(() => getUniqueUser(), []);
  const currentUserId = getEntityId(profileUser) || getEntityId(uniqueUser);
  const createdBy = profileUser?.userActivateAttribute || uniqueUser?.userActivateAttribute;
  const recommendedPlanContent = getRecommendedPlanContent(recommendedPlan);

  useEffect(() => {
    selectedPlanProductCurrentPageRef.current = selectedPlanProductCurrentPage;
  }, [selectedPlanProductCurrentPage]);

  useEffect(() => {
    if (!companyId) return;

    const fetchPlans = async () => {
      const pricingKey = recommendedPlanPricing
        ? `${companyId}-${recommendedPlanPricing?.plan?.code || ""}-${recommendedPlanPricing?.pricingInput?.amount || 0}`
        : null;

      if (recommendedPlanPricing && createdBusinessPlanKeyRef.current !== pricingKey) {
        createdBusinessPlanKeyRef.current = pricingKey;
        await dispatch(createBusinessPlanAction(companyId, {
          ...recommendedPlanPricing,
          createdBy,
        }));
      }

      dispatch(getPlanProductAction(companyId, 1, 50, selectedPlanProductCurrentPageRef.current)).catch(() => undefined);
    };

    fetchPlans();
  }, [companyId, createdBy, dispatch, recommendedPlanPricing]);

  useEffect(() => {
    if (!selectedPlan && visibleBusinessPlans.length > 0) {
      const selectedProductPlan = visibleBusinessPlans.find(
        plan => getPlanRouteParam(plan) === getPlanRouteParam(selectedPlanProduct)
      );
      const recommendedSelectedPlan = visibleBusinessPlans.find(
        plan => getPlanRouteParam(plan) === getPlanRouteParam(recommendedBusinessPlan)
      );
      const initialPlan =
        recommendedSelectedPlan ||
        (isBusinessPlan(selectedPlanProduct) ? selectedProductPlan : null) ||
        visibleBusinessPlans[0];

      setSelectedPlan(getPlanRouteParam(initialPlan));
      dispatch(selectedPlanProductAction(initialPlan, 1));
    }
  }, [dispatch, recommendedBusinessPlan, selectedPlan, selectedPlanProduct, setSelectedPlan, visibleBusinessPlans]);

  useEffect(() => {
    if (!comparisonOpen) return undefined;

    const handleEscape = event => {
      if (event.key === "Escape") {
        setComparisonOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [comparisonOpen]);

  const handleContinuePlan = async () => {
    if (navigationDisabled || continueLoader) return;

    const planRouteParam = getPlanRouteParam(activePlan);
    if (!planRouteParam || !companyId) return;

    setContinueLoader(true);
    try {
      if (isRequestQuotePlan) {
        const planCreatedById = getEntityId(activePlan?.createdBy);
        const participantId = planCreatedById && planCreatedById !== currentUserId
          ? planCreatedById
          : "";
        const message = `I am interested to receive a quote for ${companyLink(activeCompany)} under the ${activePlan?.name}`;
        await sendPublicSupportMessage({
          message,
          navigate,
          participantId,
        });
        return;
      }

      const resp = await dispatch(getSingleSubscriptonPlan(companyId, planRouteParam, selectedPlanProductCurrentPage));
      const fullPlan = resp?.status ? resp?.data : activePlan;
      const selectedPlanType = parseInt(stripeType || fullPlan?.planType?.value);
      const selectedPaymentMethod =
        paymentMethodSelection ||
        fullPlan?.supportedPaymentMethod?.[0]?.value ||
        SUPPORTED_PAYMENT_METHOD.PAY_WITH_CARD;

      dispatch(updateStripePayableAmountAction(0));
      dispatch(setPaymentMethodSelection(selectedPaymentMethod));

      if (
        selectedPlanType === SUBSCRIPTION_MODULE_TYPE.PAIDUPCAPITAL ||
        selectedPlanType === SUBSCRIPTION_MODULE_TYPE.FUNDS
      ) {
        dispatch(manualPaymentAction(fullPlan?.price));
      } else {
        dispatch(manualPaymentAction(0));
      }

      dispatch(subscriptionPlan(fullPlan));
      if (resp?.status) setTermsPlan(fullPlan);
    } finally {
      setContinueLoader(false);
    }
  };

  if (termsPlan) {
    return (
      <TermsConfirmationStep
        plan={termsPlan}
        onBack={() => setTermsPlan(null)}
        onContinue={onContinue}
      />
    );
  }

  if (detailPlan) {
    return (
      <>
        <StepIntroCard step={5} />
        <ActivateBusinessPlanDetail
          plan={detailPlan}
          isQuotePlan={getBusinessPlanDisplayMode(detailPlan) === BUSINESS_PLAN_DISPLAY_MODE.QUOTATION}
          fallbackImage={PLAN_CARD_IMAGES[visibleBusinessPlans.findIndex(plan => getPlanRouteParam(plan) === getPlanRouteParam(detailPlan)) % PLAN_CARD_IMAGES.length] || PLAN_CARD_IMAGES[0]}
          onBack={() => setDetailPlan(null)}
        />
      </>
    );
  }

  return (
    <>
      <StepIntroCard step={5} />
      {comparisonOpen && (
        <PlanComparisonModal
          onClose={() => setComparisonOpen(false)}
          planName={activePlan?.name}
        />
      )}
      {loadProductPlanList ? (
        <div className="activate-plan-loading">
          <span />
          <span />
          <span />
        </div>
      ) : visibleBusinessPlans.length > 0 ? (
        <>
          <PlanQualificationCard content={recommendedPlanContent} />
          <div className={`activate-plan-list ${shouldReservePlanBadgeSpace ? "activate-plan-list-no-toolbar" : ""}`}>
            {visibleBusinessPlans.map((plan, index) => {
              const planKey = getPlanRouteParam(plan);

              return (
                <PlanOptionCard
                  key={planKey || index}
                  plan={plan}
                  recommended={getPlanRouteParam(recommendedBusinessPlan) === planKey}
                  selected={getPlanRouteParam(activePlan) === planKey}
                  onSelect={() => {
                    setSelectedPlan(planKey);
                    dispatch(selectedPlanProductAction(plan, 1));
                  }}
                  onLearnMore={() => {
                    setSelectedPlan(planKey);
                    setDetailPlan(plan);
                  }}
                />
              );
            })}
          </div>
          <div className="activate-price-note">
            <InfoIcon />
            <span>Price may be different when you pay.</span>
          </div>
          {shouldShowComparePlans && (
            <div className="activate-plan-toolbar activate-plan-toolbar-bottom">
              <button type="button" className="activate-compare-button" onClick={() => setComparisonOpen(true)}>
                <CompareIcon />
                Compare plans
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="activate-empty-state">No plan found.</div>
      )}
      <div className="activate-actions">
        <button type="button" className="activate-back" onClick={onBack} disabled={navigationDisabled || continueLoader}>
          Back
        </button>
        <button
          type="button"
          className="activate-continue"
          onClick={handleContinuePlan}
          disabled={navigationDisabled || loadProductPlanList || continueLoader || !getPlanRouteParam(activePlan)}
        >
          {continueLoader ? "Loading..." : isRequestQuotePlan ? "Request a quote" : "Continue"}
        </button>
      </div>
    </>
  );
}

function ShareholdersDirectorsStep({ companyId, onBack, onContinue, onDataChange, navigationDisabled = false }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { shareHolder, pendingShareholderList, loadShareholder } = useSelector(state => state.shareHolder);
  const { officers, pendingOfficerList, loadOfficer } = useSelector(state => state.officers);
  const { stakeholderMyInfoCallbackUrl, pendingCompanySelected, selectedCompany } = useSelector(state => state.company);
  const [activeTab, setActiveTab] = useState("shareholders");
  const [manualFormType, setManualFormType] = useState(null);
  const [expandedStakeholderId, setExpandedStakeholderId] = useState(null);
  const [manualAddLoading, setManualAddLoading] = useState(false);
  const [continueLoading, setContinueLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteStakeholder, setDeleteStakeholder] = useState(null);
  const [myInfoCompanyData, setMyInfoCompanyData] = useState(null);
  const [myInfoLocalStakeholders, setMyInfoLocalStakeholders] = useState([]);
  const [infoModalShow, setInfoModalShow] = useState(false);
  const [infoModalType, setInfoModalType] = useState(null);
  const [stakeholderError, setStakeholderError] = useState("");
  const handledMyInfoCallbackRef = useRef(null);
  const activeCompanyForFlow = pendingCompanySelected || selectedCompany || {};
  const typeMeta = getTypeMeta(activeTab);
  const refreshRecommendedPlan = useCallback(async companyData => {
    const primaryActivity = companyData?.primaryActivity;
    const aboutPrompt = companyData?.aboutPrompt || companyData?.about || "";
    const companyDescription = companyData?.about || aboutPrompt;

    if (!primaryActivity || !companyDescription) return;

    await dispatch(getRecommendedPlanAction({
      primaryActivity,
      secondaryActivity: companyData?.secondaryActivity || "",
      aboutCompany: aboutPrompt,
      companyDescription,
    }, companyData?.aumCapitalHeld || 0));
  }, [dispatch]);
  const shareholderItems = useMemo(
    () => [...(Array.isArray(shareHolder) ? shareHolder : []), ...(Array.isArray(pendingShareholderList) ? pendingShareholderList : [])]
      .map(item => mapApiStakeholder(item, "shareholders"))
      .filter(item => item.id || item.name || item.email),
    [pendingShareholderList, shareHolder]
  );
  const directorItems = useMemo(
    () => [...(Array.isArray(officers) ? officers : []), ...(Array.isArray(pendingOfficerList) ? pendingOfficerList : [])]
      .map(item => mapApiStakeholder(item, "directors"))
      .filter(item => item.id || item.name || item.email),
    [officers, pendingOfficerList]
  );
  const stakeholders = useMemo(
    () => [...shareholderItems, ...directorItems, ...myInfoLocalStakeholders],
    [directorItems, myInfoLocalStakeholders, shareholderItems]
  );
  const visibleStakeholders = useMemo(
    () => stakeholders.filter(item => item.type === activeTab),
    [activeTab, stakeholders]
  );
  const isLoading = activeTab === "shareholders" ? loadShareholder : loadOfficer;

  useEffect(() => {
    if (!companyId) return;
    dispatch(getShareHolderList(companyId));
    dispatch(getOfficerList(companyId));
  }, [companyId, dispatch]);

  useEffect(() => {
    onDataChange?.({
      shareholders: stakeholders.filter(item => item.type === "shareholders"),
      directors: stakeholders.filter(item => item.type === "directors"),
      sources: {
        shareholders: myInfoLocalStakeholders.some(item => item.type === "shareholders") ? "myinfo" : "manual",
        directors: myInfoLocalStakeholders.some(item => item.type === "directors") ? "myinfo" : "manual",
      },
    });
  }, [myInfoLocalStakeholders, onDataChange, stakeholders]);

  useEffect(() => {
    const callbackUrl = stakeholderMyInfoCallbackUrl?.callbackUrl || location.search;
    const callbackParams = parseCallbackParams(callbackUrl);
    const callbackKey = callbackParams.result || callbackParams.id;

    if (!callbackParams.status || !callbackKey || handledMyInfoCallbackRef.current === callbackKey) return;
    handledMyInfoCallbackRef.current = callbackKey;
    refreshRecommendedPlan(activeCompanyForFlow);

    dispatch(setStakeholderMyInfoCallbackUrl({
      callbackUrl,
      status: callbackParams.status,
      result: callbackParams.result,
      id: callbackParams.id,
    }));

    if (callbackParams.status !== "success" || !callbackParams.result) {
      setMyInfoCompanyData(null);
      setMyInfoLocalStakeholders([]);
      return;
    }

    dispatch(submitStakeholderMyInfoResultAction(callbackParams.result)).then(async resp => {
      if (!resp?.status) {
        setMyInfoCompanyData(null);
        setMyInfoLocalStakeholders([]);
        return;
      }

      const cleanMyInfoResponse = getCleanMyInfoResponse(resp);
      const importedStakeholders = getMyInfoStakeholders(cleanMyInfoResponse);
      const existingKeys = new Set(stakeholders.map(item => `${item.type}:${getStakeholderCompareKey(item)}`));
      const uniqueStakeholders = importedStakeholders.filter(item => {
        const compareKey = getStakeholderCompareKey(item);
        const typedKey = `${item.type}:${compareKey}`;

        if (!compareKey || existingKeys.has(typedKey)) return false;
        existingKeys.add(typedKey);
        return true;
      });
      const companyName = getMyInfoCompanyName(cleanMyInfoResponse);

      if (companyName && companyId) {
        const companyResp = await dispatch(addCompany({
          id: companyId,
          companyName,
        }, false));

        await refreshRecommendedPlan({
          ...activeCompanyForFlow,
          companyName,
        });

        if (!companyResp?.status) {
          setMyInfoCompanyData(null);
        }
      }

      if (uniqueStakeholders.length) {
        setMyInfoLocalStakeholders(prev => [...prev, ...uniqueStakeholders]);
        setStakeholderError("");
        setActiveTab(uniqueStakeholders[0].type || "shareholders");
        setExpandedStakeholderId(uniqueStakeholders[0].id);
      }

      setMyInfoCompanyData(cleanMyInfoResponse?.data || null);
    });
  }, [activeCompanyForFlow, companyId, dispatch, location.search, refreshRecommendedPlan, stakeholderMyInfoCallbackUrl, stakeholders]);

  const options = [
    {
      key: "manual",
      title: "Add manually",
      description: "Enter details manually",
      icon: "person",
    },
    {
      key: "myinfo",
      title: "Fetch from myinfo",
      // description: "Import details from myinfo",
      description: "Coming soon",
      icon: "",
      img: "/assets/img/myinfo-logo.svg"
    },
  ];

  const handleTabChange = tab => {
    setActiveTab(tab);
    setManualFormType(null);
    setExpandedStakeholderId(null);
    setStakeholderError("");
  };

  const handleAddStakeholder = async entry => {
    if (manualAddLoading) return;

    if (!companyId) {
      dispatch(setFailedMessage("Please save business identity before adding stakeholders."));
      return;
    }

    setManualAddLoading(true);
    try {
      const payload = buildStakeholderPayload(entry, entry.type, companyId);
      const resp = entry.type === "directors"
        ? await dispatch(addOfficerAction(payload))
        : await dispatch(addShareAction(payload));

      if (resp?.status) {
        setStakeholderError("");
        await dispatch(getShareHolderList(companyId));
        await dispatch(getOfficerList(companyId));
        setExpandedStakeholderId(getApiStakeholderId(resp?.data) || null);
        setManualFormType(null);
      } else {
        dispatch(setFailedMessage(resp?.data || "Something wen't wrong"));
      }
    } finally {
      setManualAddLoading(false);
    }
  };

  const handleDeleteStakeholder = item => {
    if (item?.myInfoData) {
      setMyInfoLocalStakeholders(prev => prev.filter(stakeholder => stakeholder.id !== item.id));
      if (expandedStakeholderId === item.id) setExpandedStakeholderId(null);
      setStakeholderError("");
      return;
    }

    if (!companyId || !item?.id) return;
    setDeleteStakeholder(item);
    setStakeholderError("");
  };

  const handleDeleteModalClose = () => {
    if (deleteLoading) return;
    setDeleteStakeholder(null);
  };

  const handleConfirmDeleteStakeholder = async () => {
    if (!companyId || !deleteStakeholder?.id || deleteLoading) return;

    setDeleteLoading(true);
    try {
      const deletePayload = {
        id: deleteStakeholder.id,
        skipRequestForCompanyCreation: true,
      };

      if (deleteStakeholder.type === "directors") {
        await dispatch(removeOfficer(companyId, deletePayload));
      } else {
        await dispatch(removeShareHolder(companyId, deletePayload));
      }

      if (expandedStakeholderId === deleteStakeholder.id) setExpandedStakeholderId(null);
      setDeleteStakeholder(null);
      setStakeholderError("");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleImportMyInfo = () => {
    if (!companyId) {
      dispatch(setFailedMessage("Please save business identity before using MyInfo."));
      return;
    }

    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.set("step", "2");
    returnUrl.searchParams.delete("status");
    returnUrl.searchParams.delete("result");
    returnUrl.searchParams.delete("state");

    dispatch(setStakeholderMyInfoCallbackUrl({
      callbackUrl: returnUrl.toString(),
      status: null,
      result: null,
      id: companyId,
    }));
    localStorage.setItem("activateStakeholderMyInfoReturnUrl", returnUrl.toString());
    window.location.href = MYINFO_AUTHORIZE_URL;
  };

  const handleContinueStep = async ({ requireStakeholder = false } = {}) => {
    if (navigationDisabled || continueLoading) return;

    if (requireStakeholder && !stakeholders.length) {
      setStakeholderError("Please add at least one shareholder or director to continue.");
      return;
    }

    setStakeholderError("");
    setContinueLoading(true);
    try {
      if (myInfoCompanyData && companyId) {
        const companyResp = await dispatch(addCompany({
          ...activeCompanyForFlow,
          id: companyId,
        }, false));

        await refreshRecommendedPlan(companyResp?.data || activeCompanyForFlow);

        if (!companyResp?.status) {
          onContinue();
          return;
        }

        const resp = await dispatch(saveFap2CompanyMyInfoDataAction({
          company: companyId,
          data: myInfoCompanyData,
        }));

        if (!resp?.status) {
          return;
        }
      }

      onContinue();
    } finally {
      setContinueLoading(false);
    }
  };

  const handleLearnMore = () => {
    setInfoModalType(activeTab === "directors" ? I_POP_OVER.ACTIVATE_BUSINESS_DIRECTOR : I_POP_OVER.ACTIVATE_BUSINESS_SHAREHOLDER);
    setInfoModalShow(true);
  };

  return (
    <div className="activate-stakeholders">
      <StepIntroCard
        step={2}
      />

      <div className="activate-tabs" role="tablist" aria-label="Shareholders and directors">
        {STAKEHOLDER_TYPES.map(item => (
          <button
            type="button"
            className={activeTab === item.key ? "active" : ""}
            onClick={() => handleTabChange(item.key)}
            role="tab"
            aria-selected={activeTab === item.key}
            key={item.key}
          >
            {item.title}
          </button>
        ))}
      </div>

      <div className="activate-stakeholder-learn">
        <span>What does being a {typeMeta.singular} involve?</span>
        <button
          type="button"
          className="activate-stakeholder-info"
          onClick={handleLearnMore}
          aria-label={`What does being a ${typeMeta.singular} involve?`}
        >
          i
        </button>
      </div>

      <div className="activate-stakeholder-options">
        {options.map(option => (
          <button
            type="button"
            className="activate-stakeholder-option"
            onClick={option.key === "manual" ? () => setManualFormType(activeTab) : null}
            // onClick={option.key === "manual" ? () => setManualFormType(activeTab) : handleImportMyInfo}
            key={option.key}
          >
            {option.icon ?
              <span className={`activate-option-icon ${option.icon}`} aria-hidden="true" />
              :
              <span className="activate-option-logo">
                <img src={option?.img} alt="" />
              </span>
            }
            <span>
              <strong>{option.title}</strong>
              <em>{option.description}</em>
            </span>
            {option.key !== "myinfo" && <b aria-hidden="true">›</b>}
          </button>
        ))}
      </div>

      <StakeholderEntryList
        entries={visibleStakeholders}
        type={activeTab}
        loading={isLoading}
        expandedId={expandedStakeholderId}
        onToggle={id => setExpandedStakeholderId(current => current === id ? null : id)}
        onDelete={handleDeleteStakeholder}
      />

      {!isLoading && !visibleStakeholders.length && (
        <div className="activate-empty-stakeholders">
          No {typeMeta.title.toLowerCase()} added yet. You can add them manually or import details via MyInfo.
        </div>
      )}

      <p className="activate-stakeholder-note">
        You can add more later from your dashboard.
      </p>

      <div className="activate-plan-limit">
        Up to 4 stakeholders included as standard
      </div>

      <button type="button" className="activate-skip" onClick={() => handleContinueStep()} disabled={navigationDisabled || continueLoading}>
        Skip for now
      </button>

      <div className={`activate-actions activate-stakeholder-actions${stakeholderError ? " has-error" : ""}`}>
        <button type="button" className="activate-back" onClick={onBack} disabled={navigationDisabled || continueLoading}>
          Back
        </button>
        {stakeholderError && (
          <p className="activate-stakeholder-error" role="alert">
            {stakeholderError}
          </p>
        )}
        <div>
          <button type="button" className="activate-continue" onClick={() => handleContinueStep({ requireStakeholder: true })} disabled={navigationDisabled || continueLoading}>
            {continueLoading ? "Saving..." : "Continue"}
          </button>
        </div>
      </div>

      {manualFormType && (
        <div className="activate-modal-backdrop" role="presentation">
          <StakeholderInviteForm
            type={manualFormType}
            onClose={() => setManualFormType(null)}
            onAdd={handleAddStakeholder}
            loading={manualAddLoading}
          />
        </div>
      )}

      {deleteStakeholder && (
        <div className="activate-modal-backdrop" role="presentation">
          <div className="activate-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="deleteStakeholderTitle">
            <div className="activate-form-titlebar">
              <h2 id="deleteStakeholderTitle">Confirm delete</h2>
              <button type="button" onClick={handleDeleteModalClose} disabled={deleteLoading}>
                Close
              </button>
            </div>
            <div className="activate-delete-body">
              <div>
                <h3>Are you sure you want to remove this promoter?</h3>
                <p>Promoter name</p>
                <strong>{deleteStakeholder?.name || "-"}</strong>
              </div>
              <div className="activate-delete-actions">
                <button
                  type="button"
                  className="activate-delete-confirm"
                  onClick={handleConfirmDeleteStakeholder}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Deleting..." : "Agree and continue"}
                </button>
                <button type="button" onClick={handleDeleteModalClose} disabled={deleteLoading}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <InfoModalStatic
        infoModalStaticShow={infoModalShow}
        infoModalStaticClose={() => setInfoModalShow(false)}
        infoModalStaicShowType={infoModalType}
      />
    </div>
  );
}

function StakeholderEntryList({ entries, type, loading, expandedId, onToggle, onDelete }) {
  if (loading) {
    return <div className="activate-empty-stakeholders">Loading {getTypeMeta(type).title.toLowerCase()}...</div>;
  }

  if (!entries.length) return null;

  return (
    <div className="activate-entry-list">
      <div className="activate-entry-list-header">
        <strong>{getTypeMeta(type).title}</strong>
        <span>{entries.length} {entries.length === 1 ? "entry" : "entries"}</span>
      </div>
      {entries.map(entry => {
        const isExpanded = expandedId === entry.id;

        return (
          <div className={`activate-entry-card ${isExpanded ? "expanded" : ""}`} key={entry.id}>
            <button type="button" className="activate-entry-summary" onClick={() => onToggle(entry.id)}>
              <strong>{entry.name}</strong>
              <span className="activate-entry-chevron" aria-hidden="true" />
            </button>

            {isExpanded && (
              <div className="activate-entry-details">
                <div>
                  <span>Status</span>
                  <strong className="activate-status-pill">{entry.status}</strong>
                </div>
                <div>
                  <span>Date added</span>
                  <strong>{entry.dateAdded}</strong>
                </div>
                <div>
                  <span>Email</span>
                  <strong>{entry.email || entry.share_email || "-"}</strong>
                </div>
                <div>
                  <span>Position</span>
                  <strong>{entry.position || getDefaultPosition(type)}</strong>
                </div>
                <div className="activate-entry-actions">
                  <button type="button" className="danger" onClick={() => onDelete(entry)}>Delete</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StakeholderInviteForm({ type, onClose, onAdd, loading }) {
  const isShareholder = type === "shareholders";
  const label = isShareholder ? "Shareholder" : "Director";
  const role = isShareholder ? "shareholder" : "director";
  const title = isShareholder ? "Add shareholder" : "Add director";
  const addLabel = isShareholder ? "Add shareholders" : "Add director";
  const [formData, setFormData] = useState({
    name: "",
    share_email: "",
    countryCode: "",
    phoneNumber: "",
    position: getDefaultPosition(type),
  });

  useEffect(() => {
    setFormData({
      name: "",
      share_email: "",
      countryCode: "",
      phoneNumber: "",
      position: getDefaultPosition(type),
    });
  }, [type]);

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitInvite = event => {
    event.preventDefault();
    onAdd({
      type,
      name: formData.name.trim(),
      share_email: formData.share_email.trim(),
      countryCode: formData.countryCode,
      phoneNumber: formData.phoneNumber.trim(),
    });
  };

  return (
    <form className="activate-stakeholder-form" role="dialog" aria-modal="true" aria-labelledby="stakeholderInviteTitle" onSubmit={handleSubmitInvite}>
      <div className="activate-form-titlebar">
        <h2 id="stakeholderInviteTitle">{title}</h2>
        <button type="button" onClick={onClose} aria-label="Close invite form">
          Close
        </button>
      </div>

      <div className="activate-modal-body">
        <div className="activate-invite-fields">
          <label className="activate-field">
            <span>{label} full name*</span>
            <input name="name" placeholder="Full name" value={formData.name} onChange={handleChange} required />
          </label>
          <label className="activate-field">
            <span>{label} email*</span>
            <input name="share_email" type="email" placeholder="Email address" value={formData.share_email} onChange={handleChange} required />
          </label>
          <label className="activate-field">
            <span>{label} country code</span>
            <select name="countryCode" value={formData.countryCode} onChange={handleChange}>
              <option value="" disabled>Select country code</option>
              {country.map(item => (
                <option value={item.dial_code} key={`${item.code}-${item.dial_code}`}>
                  {item.name} ({item.dial_code})
                </option>
              ))}
            </select>
          </label>
          <label className="activate-field">
            <span>{label} mobile number</span>
            <input name="phoneNumber" type="tel" placeholder="Mobile number" value={formData.phoneNumber} onChange={handleChange} />
          </label>
        </div>

        <p className="activate-important-note">
          <strong>Important note:</strong> We will firstly send an invitation to this person to invite him/her to this business dashboard. You will be informed once the invitation is accepted by this person. You can then message him/her directly to discuss appointing him/her as a {role}.
        </p>

        <button type="submit" className="activate-add-stakeholder" disabled={loading}>
          {loading ? "Adding..." : addLabel}
        </button>
      </div>
    </form>
  );
}

function GoalsObjectivesStep({ company, companyName, onBack, onContinue, onSkip, onDataChange, navigationDisabled = false }) {
  const dispatch = useDispatch();
  const {
    activateAddedCompany,
    addCompanyTrigger,
    companyType: companyTypeFromState,
    pendingCompanySelected,
    selectedCompany,
  } = useSelector(state => state.company);
  const {
    goalsList,
    goalsListLoader,
    aiChips,
    campaignObjective,
    aiFilterResponse,
    isObjectiveFilters,
    campaignDetailToSend,
  } = useSelector(state => state.peopleai);
  const companyType = companyTypeFromState ?? 1;
  const activeCompany = company || pendingCompanySelected || selectedCompany;
  const activeCompanyId = getCompanyId(activeCompany);
  const isCompanySetupFlow = (addCompanyTrigger || activateAddedCompany || activeCompanyId) && companyType === 1;
  const [subStep, setSubStep] = useState(1);
  const [selectedGoalId, setSelectedGoalId] = useState(null);
  const [customGoals, setCustomGoals] = useState([]);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [chips, setChips] = useState([]);
  const [selectedObjectives, setSelectedObjectives] = useState([]);
  const [customObjective, setCustomObjective] = useState("");
  const [showCustomObjectiveInput, setShowCustomObjectiveInput] = useState(false);
  const [statement, setStatement] = useState({ title: "", body: "" });
  const [statementDraft, setStatementDraft] = useState(statement);
  const [isStatementEditing, setIsStatementEditing] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    role: [],
    seniority: [],
    location: [],
    industry: [],
  });
  const [audience, setAudience] = useState({
    role: [],
    seniority: [],
    location: [],
    industry: [],
  });
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const allGoalOptions = useMemo(
    () => {
      const apiGoals = Array.isArray(goalsList) && goalsList.length ? goalsList : goalOptions.map(goal => ({
        id: goal.key,
        name: goal.title,
        description: goal.description,
        img: goal.iconUrl,
      }));

      const existingKeys = new Set(apiGoals.map(goal => String(goal.id || goal.name || goal.title).toLowerCase()));
      const uniqueCustomGoals = customGoals.filter(goal => {
        const key = String(goal.id || goal.name || goal.title).toLowerCase();
        return key && !existingKeys.has(key);
      });

      return [...uniqueCustomGoals, ...apiGoals];
    },
    [customGoals, goalsList]
  );
  const selectedGoalData = allGoalOptions.find(goal => String(goal.id) === String(selectedGoalId)) || null;
  const businessName = companyName || "your business";
  const encode = value => encodeURIComponent(value || "");
  const chipsQuery = selectedObjectives.map(chip => `chips[]=${encodeURIComponent(chip)}`).join("&");
  const buildFilterQuery = value =>
    Array.isArray(value)
      ? value.map(item => encodeURIComponent(item)).join(",")
      : encodeURIComponent(value || "");
  const joinFilterLabels = value => Array.isArray(value) ? value.join(", ") : value || "";
  const splitCSV = value => {
    if (Array.isArray(value)) return value.map(item => String(item || "").trim()).filter(Boolean);
    return String(value || "").split(",").map(item => item.trim()).filter(Boolean);
  };

  useEffect(() => {
    if (!activeCompany?.companyName) return;

    const query = `?path=api/generate-goal&companyName=${encode(activeCompany.companyName)}&type=${companyType}&companyDescription=${encode(activeCompany?.about || "")}`;
    dispatch(getMergedGoalsAction("company", activeCompany, query));
  }, [activeCompany, companyType, dispatch]);

  useEffect(() => {
    const nextChips = Array.isArray(aiChips) ? aiChips : [];
    setChips(nextChips);
    setSelectedObjectives(nextChips.slice(0, 5));
  }, [aiChips]);

  useEffect(() => {
    if (!campaignObjective) return;

    const nextStatement = {
      title: campaignObjective?.title || "",
      body: campaignObjective?.description || "",
    };
    setStatement(nextStatement);
    setStatementDraft(nextStatement);
  }, [campaignObjective]);

  useEffect(() => {
    const nextOptions = {
      role: splitCSV(aiFilterResponse?.role || aiFilterResponse?.targetting),
      seniority: splitCSV(aiFilterResponse?.seniority),
      location: splitCSV(aiFilterResponse?.country || aiFilterResponse?.location),
      industry: splitCSV(aiFilterResponse?.industry),
    };
    setFilterOptions(nextOptions);
    setAudience({
      role: [],
      seniority: [],
      location: [],
      industry: [],
    });
  }, [aiFilterResponse]);

  useEffect(() => {
    onDataChange?.({
      goal: selectedGoalData,
      objectives: selectedObjectives,
      statement: {
        title: statement.title,
        body: statement.body || statement.description,
      },
      audience,
      campaign: campaignDetailToSend,
      isCompanySetupFlow,
    });
  }, [audience, campaignDetailToSend, isCompanySetupFlow, onDataChange, selectedGoalData, selectedObjectives, statement]);

  const toggleObjective = objective => {
    setSelectedObjectives(prev => {
      if (prev.includes(objective)) {
        return prev.length === 1 ? chips.slice(0, 5) : prev.filter(item => item !== objective);
      }

      return prev.length < 8 ? [...prev, objective] : prev;
    });
  };

  const addCustomObjective = () => {
    const value = customObjective.trim();
    if (!value || chips.some(chip => chip.toLowerCase() === value.toLowerCase()) || selectedObjectives.length >= 8) return;
    setChips(prev => [value, ...prev]);
    setSelectedObjectives(prev => [...prev, value]);
    setCustomObjective("");
    setShowCustomObjectiveInput(false);
  };

  const cancelCustomObjective = () => {
    setCustomObjective("");
    setShowCustomObjectiveInput(false);
  };

  const addCustomGoal = goal => {
    const title = goal.title.trim();
    const description = goal.description.trim();
    const existingGoal = allGoalOptions.find(item =>
      String(item.name || item.title || "").trim().toLowerCase() === title.toLowerCase()
    );

    if (existingGoal) {
      const existingId = existingGoal.id || existingGoal.key || existingGoal.name;
      setSelectedGoalId(existingId);
      dispatch(setObjectiveFilter(existingGoal));
      setShowGoalModal(false);
      return;
    }

    const newGoal = {
      id: `custom-goal-${Date.now()}`,
      name: title,
      title,
      description,
      removable: true,
    };
    setCustomGoals(prev => [newGoal, ...prev]);
    dispatch(setObjectiveFilter(newGoal));
    setSelectedGoalId(newGoal.id);
    setShowGoalModal(false);
  };

  const handleGoalSelect = goalKey => {
    const nextGoal = allGoalOptions.find(goal => String(goal.id) === String(goalKey));
    setSelectedGoalId(goalKey);
    dispatch(setObjectiveFilter(nextGoal || null));
  };

  const toggleAudience = (group, value) => {
    setAudience(prev => {
      const selected = prev[group];
      const isSelected = selected.includes(value);
      return {
        ...prev,
        [group]: isSelected
          ? selected.filter(item => item !== value)
          : selected.length < 3 || group === "location"
            ? [...selected, value]
            : selected,
      };
    });
  };

  const addAudienceOption = (group, value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return false;

    const alreadyExists = filterOptions[group].some(option => option.toLowerCase() === trimmedValue.toLowerCase());
    if (!alreadyExists) {
      setFilterOptions(prev => ({
        ...prev,
        [group]: [...prev[group], trimmedValue],
      }));
    }

    setAudience(prev => ({
      ...prev,
      [group]: prev[group].includes(trimmedValue) ? prev[group] : [...prev[group], trimmedValue],
    }));
    return true;
  };

  const startStatementEdit = () => {
    setStatementDraft(statement);
    setIsStatementEditing(true);
  };

  const cancelStatementEdit = () => {
    setStatementDraft(statement);
    setIsStatementEditing(false);
  };

  const updateStatementDraft = event => {
    const { name, value } = event.target;
    setStatementDraft(prev => ({ ...prev, [name]: value }));
  };

  const saveStatementEdit = event => {
    event.preventDefault();
    const nextStatement = {
      title: statementDraft.title.trim(),
      body: statementDraft.body.trim(),
    };

    if (!nextStatement.title || !nextStatement.body) return;

    setStatement(nextStatement);
    setStatementDraft(nextStatement);
    dispatch(updateObjectiveTitle({ title: nextStatement.title, description: nextStatement.body }));
    setIsStatementEditing(false);
  };

  const handlePrimaryAction = async () => {
    if (navigationDisabled || isStepLoading) return;

    setErrorMessage("");

    if (subStep === 1) {
      if (!selectedGoalData) {
        setErrorMessage("Please select at least one goal to proceed");
        return;
      }

      setLoadingLabel("Crafting objectives for you based on your selected goal");
      const query = `path=api/generate-chips&title=${encode(selectedGoalData?.name)}&description=${encode(selectedGoalData?.description)}&companyDescription=${encode(activeCompany?.about || "")}`;
      const resp = await dispatch(getAiAction(`?${query}`));
      setLoadingLabel("");
      if (resp?.status) {
        dispatch(setObjectiveFilter(selectedGoalData));
        setSubStep(2);
      }
      return;
    }

    if (subStep === 2) {
      if (selectedObjectives.length < 3) {
        setErrorMessage("Please select at least 3 chips.");
        return;
      }

      setLoadingLabel("Generating your objective statement");
      const query = `path=api/generate-objectives&title=${encode(selectedGoalData?.name)}&description=${encode(selectedGoalData?.description)}&${chipsQuery}&companyDescription=${encode(activeCompany?.about || "")}&type=${companyType}`;
      const resp = await dispatch(getAIObjectivesAction(`?${query}`));
      setLoadingLabel("");
      const snapshot = { ...selectedGoalData, chips: selectedObjectives };
      dispatch(setObjectiveFilter(snapshot));
      if (resp?.status) setSubStep(3);
      return;
    }

    if (subStep === 3) {
      const objectiveTitle = campaignObjective?.title || statement.title;
      const objectiveDescription = campaignObjective?.description || statement.body;

      if (!objectiveTitle) {
        setErrorMessage("Please select or enter an objective statement.");
        return;
      }

      setLoadingLabel("Crafting audience for you based on your objective");
      const query = `path=api/generate-targeting&title=${encode(selectedGoalData?.name)}&description=${encode(selectedGoalData?.description)}&${chipsQuery}&objectiveTitle=${encode(objectiveTitle)}&objectiveDescription=${encode(objectiveDescription)}&type=${companyType}`;
      const resp = await dispatch(getAiFilterAction(`?${query}`));
      setLoadingLabel("");
      dispatch(setObjectiveFilter({
        ...selectedGoalData,
        objectiveTitle,
        objectiveDescription,
        chips: selectedObjectives,
      }));
      if (resp?.status) setSubStep(4);
      return;
    }

    const missingFilters = ["role", "seniority", "location", "industry"].filter(key => !audience[key]?.length);
    if (missingFilters.length) {
      setErrorMessage(`Please select: ${missingFilters.join(", ")}`);
      return;
    }

    const snapshotPayload = {
      title: selectedGoalData?.name,
      description: selectedGoalData?.description,
      objectiveTitle: campaignObjective?.title || statement.title,
      objectiveDescription: campaignObjective?.description || statement.body,
      chips: selectedObjectives,
      isVerified: verifiedOnly,
      ...audience,
    };
    dispatch(setObjectiveFilter(snapshotPayload));
    setLoadingLabel("Finding matching profiles and saving your campaign draft");
    const locationQuery = (snapshotPayload.location || []).map(location => encodeURIComponent(location)).join(",");
    const payload = `?path=api/search&title=${encode(snapshotPayload.title)}&description=${encode(snapshotPayload.description)}&objectiveTitle=${encode(snapshotPayload.objectiveTitle)}&objectiveDescription=${encode(snapshotPayload.objectiveDescription)}&${chipsQuery}${locationQuery ? `&country=${locationQuery}` : ""}&role=${buildFilterQuery(snapshotPayload.role)}&industry=${buildFilterQuery(snapshotPayload.industry)}&seniority=${buildFilterQuery(snapshotPayload.seniority)}&isverified=${verifiedOnly}&page=1&pageSize=10`;
    const payloadData = {
      title: snapshotPayload.title,
      description: snapshotPayload.description,
      seniority: joinFilterLabels(snapshotPayload.seniority),
      location: joinFilterLabels(snapshotPayload.location),
      industry: joinFilterLabels(snapshotPayload.industry),
      role: joinFilterLabels(snapshotPayload.role),
      isVerified: verifiedOnly,
      objectiveTitle: snapshotPayload.objectiveTitle,
      objectiveDescription: snapshotPayload.objectiveDescription,
      chips: chipsQuery,
    };
    const resp = await dispatch(getLeadsProfileAction(payload, null, payloadData, null, true, true));
    if (resp?.status && isCompanySetupFlow) {
      const campaignDetail = resp?.data?.campaign || {};
      await dispatch(addCampaignAiAction({
        campaign: {
          ...campaignDetail,
          company: activeCompanyId,
          seniority: snapshotPayload.seniority,
          location: snapshotPayload.location,
          industry: snapshotPayload.industry,
          targetting: snapshotPayload.role,
          onlyVerifiedUsers: verifiedOnly,
          name: campaignDetail?.name || "",
          description: campaignDetail?.description || "",
          tags: campaignDetail?.tags || "",
          status: "draft",
        },
      }));
      onContinue();
    }
    setLoadingLabel("");
  };

  const handleLocalBack = () => {
    if (navigationDisabled || isStepLoading) return;

    if (subStep === 1) {
      onBack();
      return;
    }
    setSubStep(step => step - 1);
  };

  const handleSkip = () => {
    if (navigationDisabled || isStepLoading) return;
    (onSkip || onContinue)?.();
  };

  const actionLabel = subStep === 1
    ? "Proceed"
    : subStep === 2
      ? "Generate objective"
      : subStep === 3
        ? "Proceed"
        : "Continue";
  const isStepLoading = Boolean(loadingLabel) || (subStep === 1 && goalsListLoader);

  return (
    <div className="activate-goals">
      {loadingLabel && <div className="activate-ai-loading">{loadingLabel}</div>}
      <StepIntroCard step={3} subStep={subStep} />

      {subStep === 1 && (
        <section className="activate-goal-section">
          <div className="activate-section-heading">
            <h3>What is your goal for {businessName}?</h3>
          </div>
          {goalsListLoader && <div className="activate-empty-state">Loading goals...</div>}
          <div className="activate-chip-group activate-goal-chip-group">
            {allGoalOptions.map(goal => {
              const goalId = goal.id || goal.key || goal.name;
              const isSelected = String(selectedGoalId) === String(goalId);
              return (
                <button
                  type="button"
                  className={isSelected ? "selected" : ""}
                  title={goal.description || goal.name || goal.title}
                  onClick={() => handleGoalSelect(goalId)}
                  key={goalId}
                >
                  {goal.name || goal.title}
                  {isSelected && <span>x</span>}
                </button>
              );
            })}
            <button type="button" className="activate-chip-add" onClick={() => setShowGoalModal(true)}>
              + Other
            </button>
          </div>
        </section>
      )}

      {subStep === 2 && (
        <section className="activate-goal-section">
          <SummaryTile title={`What is your goal for ${businessName}?`} value={selectedGoalData?.name} onClick={() => setSubStep(1)} />
          <div className="activate-section-heading with-count">
            <h3>What is your objective?</h3>
            <span>{selectedObjectives.length}/{chips.length} selected</span>
          </div>
          <p className="activate-goal-copy">Select your focus areas. Choose topics you want to advise on. This helps us match you with the right clients.</p>
          <ChipGroup options={chips} selected={selectedObjectives} onToggle={toggleObjective} />
          {!showCustomObjectiveInput ? (
            <button type="button" className="activate-chip-add activate-objective-add" onClick={() => setShowCustomObjectiveInput(true)}>
              + Other
            </button>
          ) : (
            <div className="activate-add-chip-row">
              <input value={customObjective} onChange={event => setCustomObjective(event.target.value)} placeholder="Add new" autoFocus />
              <button type="button" onClick={cancelCustomObjective}>Cancel</button>
              <button type="button" onClick={addCustomObjective}>Save</button>
            </div>
          )}
          <div className="activate-dashed-note">We will generate an objective statement to guide your campaign.</div>
        </section>
      )}

      {subStep === 3 && (
        <section className="activate-goal-section">
          <SummaryTile title={`What is your goal for ${businessName}?`} value={selectedGoalData?.name} onClick={() => setSubStep(1)} />
          <SummaryTile title="What is your objective?" value={selectedObjectives.join(", ")} onClick={() => setSubStep(2)} />
          <div className="activate-section-heading">
            <h3>Your objective statement</h3>
          </div>
          <div className={`activate-statement-card ${isStatementEditing ? "editing" : ""}`}>
            {isStatementEditing ? (
              <form className="activate-statement-form" onSubmit={saveStatementEdit}>
                <label className="activate-field">
                  <span>Objective title</span>
                  <input
                    name="title"
                    value={statementDraft.title}
                    onChange={updateStatementDraft}
                    placeholder="Enter objective title"
                  />
                </label>
                <label className="activate-field">
                  <span>Objective description</span>
                  <textarea
                    name="body"
                    value={statementDraft.body}
                    onChange={updateStatementDraft}
                    placeholder="Enter objective description"
                  />
                </label>
                <div className="activate-statement-actions">
                  <button type="button" onClick={cancelStatementEdit}>Cancel</button>
                  <button type="submit" disabled={!statementDraft.title.trim() || !statementDraft.body.trim()}>Save</button>
                </div>
              </form>
            ) : (
              <>
                <button type="button" className="activate-statement-edit" onClick={startStatementEdit} aria-label="Edit objective statement">Edit</button>
                <h3>{statement.title}</h3>
                <p>{statement.body || statement.description}</p>
              </>
            )}
          </div>
        </section>
      )}

      {subStep === 4 && (
        <section className="activate-goal-section">
          <SummaryTile title={`What is your goal for ${businessName}?`} value={selectedGoalData?.name} onClick={() => setSubStep(1)} />
          <SummaryTile title="What is your objective?" value={statement.title} description={statement.body} onClick={() => setSubStep(2)} />
          <div className="activate-section-heading">
            <h3>Who are you looking for?</h3>
          </div>
          <p className="activate-goal-copy">Most partners start with a focused audience to get better matches.</p>
          <AudienceGroup title="Role" options={filterOptions.role} selected={audience.role} onToggle={value => toggleAudience("role", value)} onAdd={value => addAudienceOption("role", value)} />
          <AudienceGroup title="Seniority" options={filterOptions.seniority} selected={audience.seniority} onToggle={value => toggleAudience("seniority", value)} onAdd={value => addAudienceOption("seniority", value)} />
          <AudienceGroup title="Location" options={filterOptions.location} selected={audience.location} onToggle={value => toggleAudience("location", value)} onAdd={value => addAudienceOption("location", value)} />
          <AudienceGroup title="Industry" options={filterOptions.industry} selected={audience.industry} onToggle={value => toggleAudience("industry", value)} onAdd={value => addAudienceOption("industry", value)} />
        </section>
      )}

      {errorMessage && <div className="activate-form-error">{errorMessage}</div>}

      <button type="button" className="activate-skip activate-goal-skip" onClick={handleSkip} disabled={navigationDisabled || isStepLoading}>
        Skip for now
      </button>

      <div className="activate-actions activate-goal-actions">
        <button type="button" className="activate-back" onClick={handleLocalBack} disabled={navigationDisabled || isStepLoading}>
          Back
        </button>
        <button type="button" className="activate-continue" onClick={handlePrimaryAction} disabled={navigationDisabled || isStepLoading}>
          {isStepLoading && <span className="activate-button-loader" aria-hidden="true" />}
          {actionLabel}
        </button>
      </div>

      {showGoalModal && (
        <div className="activate-modal-backdrop" role="presentation">
          <AddGoalModal
            onClose={() => setShowGoalModal(false)}
            onSave={addCustomGoal}
          />
        </div>
      )}
    </div>
  );
}

function AddGoalModal({ onClose, onSave }) {
  const [goal, setGoal] = useState({
    title: "",
    description: "",
  });
  const canSave = goal.title.trim() && goal.description.trim();

  const handleChange = event => {
    const { name, value } = event.target;
    setGoal(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (!canSave) return;
    onSave({
      title: goal.title.trim(),
      description: goal.description.trim(),
    });
  };

  return (
    <form className="activate-add-goal-modal" role="dialog" aria-modal="true" aria-labelledby="addGoalTitle" onSubmit={handleSubmit}>
      <div className="activate-add-goal-handle" aria-hidden="true" />
      <div className="activate-add-goal-titlebar">
        <h2 id="addGoalTitle">Add new goal</h2>
        <button type="button" onClick={onClose} aria-label="Close add goal form">
          x
        </button>
      </div>
      <div className="activate-add-goal-body">
        <label className="activate-field">
          <span>Goal title</span>
          <input name="title" value={goal.title} onChange={handleChange} placeholder="Enter title" />
        </label>
        <label className="activate-field">
          <span>Description</span>
          <textarea name="description" value={goal.description} onChange={handleChange} placeholder="Describe the goal" />
        </label>
        <div className="activate-add-goal-actions">
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={!canSave}>Save</button>
        </div>
      </div>
    </form>
  );
}

function SummaryTile({ title, value, description, onClick }) {
  const Tag = onClick ? "button" : "div";

  return (
    <Tag type={onClick ? "button" : undefined} className={`activate-summary-tile ${onClick ? "clickable" : ""}`} onClick={onClick}>
      <span className="activate-summary-check" aria-hidden="true" />
      <div>
        <strong>{title}</strong>
        <p>{value}</p>
        {description && <em>{description}</em>}
      </div>
    </Tag>
  );
}

function ChipGroup({ options, selected, onToggle }) {
  return (
    <div className="activate-chip-group">
      {options.map(option => {
        const isSelected = selected.includes(option);
        return (
          <button
            type="button"
            className={isSelected ? "selected" : ""}
            onClick={() => onToggle(option)}
            key={option}
          >
            {option}
            {isSelected && <span>x</span>}
          </button>
        );
      })}
    </div>
  );
}

function AudienceGroup({ title, options, selected, onToggle, onAdd }) {
  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState("");

  const handleSave = () => {
    if (!newValue.trim()) return;
    const added = onAdd(newValue);
    if (!added) return;
    setNewValue("");
    setIsAdding(false);
  };

  const handleCancel = () => {
    setNewValue("");
    setIsAdding(false);
  };

  return (
    <div className="activate-audience-group">
      <h4>{title}</h4>
      <ChipGroup options={options} selected={selected} onToggle={onToggle} />
      {isAdding ? (
        <div className="activate-audience-add-row">
          <input
            value={newValue}
            onChange={event => setNewValue(event.target.value)}
            placeholder={`Add ${title.toLowerCase()}...`}
          />
          <button type="button" onClick={handleCancel}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={!newValue.trim()}>Add</button>
        </div>
      ) : (
        <button type="button" className="activate-add-small" onClick={() => setIsAdding(true)}>+ Add</button>
      )}
    </div>
  );
}

function ActivityDropdown({
  name,
  label,
  placeholder,
  options,
  value,
  register,
  setValue,
  requiredMessage,
  error,
  onSelect,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const dropdownRef = useRef(null);
  const listId = `${name}Options`;
  const selectedOption = options.find(activity => activity.title === value);
  const registration = register(name, requiredMessage ? { required: requiredMessage } : undefined);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;
    return options.filter(activity => activity.title.toLowerCase().includes(normalizedQuery));
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsideClick = event => {
      if (!dropdownRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = event => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const handleSelect = nextValue => {
    setValue(name, nextValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    if (typeof onSelect === "function") {
      onSelect(nextValue);
    }
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="activate-field activate-dropdown-field" ref={dropdownRef}>
      <span id={`${name}Label`}>{label}</span>
      <input type="hidden" {...registration} value={value || ""} readOnly />
      <button
        type="button"
        className={`activate-activity-select ${open ? "open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${name}Label`}
        aria-controls={listId}
        onClick={() => setOpen(isOpen => !isOpen)}
      >
        <span>{selectedOption?.title || placeholder}</span>
      </button>
      {open && (
        <div className="activate-activity-menu">
          <input
            className="activate-activity-search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search activity"
            autoFocus
          />
          <div className="activate-activity-options" id={listId} role="listbox" aria-labelledby={`${name}Label`}>
            {filteredOptions.map(activity => (
              <button
                type="button"
                className={activity.title === value ? "selected" : ""}
                key={activity.code}
                role="option"
                aria-selected={activity.title === value}
                onClick={() => handleSelect(activity.title)}
              >
                {activity.title}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="activate-activity-empty">No activities found</div>
            )}
          </div>
        </div>
      )}
      {error && <small>{error.message}</small>}
    </div>
  );
}

function BusinessInfoStep({
  register,
  errors,
  watch,
  setValue,
  setError,
  clearErrors,
  getValues,
  recommendedPlan,
  logoUploading,
  uploadFiles,
  removeFile,
  onGeneratingChange,
}) {
  const dispatch = useDispatch();
  const [aboutLoader, setAboutLoader] = useState(false);
  const [showFinalDescription, setShowFinalDescription] = useState(false);
  const isGeneratingRef = useRef(false);
  const isUserTypingRef = useRef(false);
  const shouldGenerateAboutRef = useRef(false);
  const generateTimerRef = useRef(null);
  const logo = watch("logo");
  const aboutPrompt = watch("aboutPrompt") || "";
  const about = watch("about") || "";
  const primaryActivity = watch("primaryActivity") || "";
  const secondaryActivity = watch("secondaryActivity") || "";
  const amountFieldMeta = getAmountFieldMeta(recommendedPlan);
  const [infoModalShow, setInfoModalShow] = useState(false);
  const activityOptions = useMemo(() => ACTIVITY, []);
  const aboutPromptRegister = register("aboutPrompt", {
    required: "About is required",
    minLength: { value: 30, message: "About must be at least 30 characters" },
    onChange: () => {
      shouldGenerateAboutRef.current = true;
      isUserTypingRef.current = true;
      setShowFinalDescription(false);
    },
  });

  const validateAbout = useCallback(({ about, primaryActivity, secondaryActivity }) => {
    const payload = `?path=api/generate-company-description&primaryActivity=${encodeURIComponent(primaryActivity)}&secondaryActivity=${encodeURIComponent(secondaryActivity || "")}&aboutUs=${encodeURIComponent(about)}&maxChars=500`;
    return dispatch(validateInputAction(payload));
  }, [dispatch]);

  const handleGenerateAbout = useCallback(async () => {
    if (showFinalDescription && !isUserTypingRef.current) return;
    if (isGeneratingRef.current) return;

    if (!primaryActivity) {
      setError("primaryActivity", { message: "Primary activity is required" });
      return;
    }

    isGeneratingRef.current = true;
    setAboutLoader(true);
    onGeneratingChange(true);

    try {
      const resp = await validateAbout({
        about: aboutPrompt,
        primaryActivity,
        secondaryActivity,
      });
      const generated = resp?.data?.companyDescription || resp?.companyDescription;

      if (!generated) {
        setError("aboutPrompt", { message: "AI generation failed" });
        return;
      }
      setValue("about", generated, { shouldDirty: true, shouldValidate: true });
      setValue("businessType", getTemporaryRandomBusinessType().value, {
        shouldDirty: true,
        shouldValidate: true,
      });
      await dispatch(getRecommendedPlanAction({
        primaryActivity,
        secondaryActivity: secondaryActivity || "",
        aboutCompany: aboutPrompt,
        companyDescription: generated,
      }, getValues("aumCapitalHeld") || 0));
      setShowFinalDescription(true);
      isUserTypingRef.current = false;
      clearErrors(["aboutPrompt", "about"]);
    } finally {
      isGeneratingRef.current = false;
      setAboutLoader(false);
      onGeneratingChange(false);
    }
  }, [
    aboutPrompt,
    clearErrors,
    dispatch,
    getValues,
    onGeneratingChange,
    primaryActivity,
    secondaryActivity,
    setError,
    setValue,
    showFinalDescription,
    validateAbout,
  ]);

  useEffect(() => {
    const trimmedPrompt = aboutPrompt.trim();

    if (!shouldGenerateAboutRef.current) {
      return undefined;
    }

    if (
      primaryActivity &&
      trimmedPrompt.length >= 30 &&
      !aboutLoader &&
      !isGeneratingRef.current
    ) {
      clearErrors("primaryActivity");
      if (generateTimerRef.current) clearTimeout(generateTimerRef.current);
      shouldGenerateAboutRef.current = false;
      generateTimerRef.current = setTimeout(handleGenerateAbout, 500);
    }

    return () => {
      if (generateTimerRef.current) clearTimeout(generateTimerRef.current);
    };
  }, [aboutLoader, aboutPrompt, clearErrors, handleGenerateAbout, primaryActivity, secondaryActivity, showFinalDescription]);

  return (
    <>
      <StepIntroCard step={1} />
      <div className="activate-form-grid">
        <label className="activate-field">
          <span>Holding company name*</span>
          <input maxLength={50} placeholder="Holding company name" {...register("companyName", { required: "Name is required" })} />
          {errors.companyName && <small>{errors.companyName.message}</small>}
        </label>
        <input type="hidden" {...register("businessType")} />
        <ActivityDropdown
          name="primaryActivity"
          label="New primary business activity*"
          placeholder="Select primary activity"
          options={activityOptions}
          value={primaryActivity}
          register={register}
          setValue={setValue}
          requiredMessage="Primary activity is required"
          error={errors.primaryActivity}
          onSelect={() => {
            shouldGenerateAboutRef.current = true;
            isUserTypingRef.current = true;
            setShowFinalDescription(false);
          }}
        />
        <ActivityDropdown
          name="secondaryActivity"
          label="New secondary business activity"
          placeholder="Select secondary activity"
          options={activityOptions}
          value={secondaryActivity}
          register={register}
          setValue={setValue}
        />
        <label className="activate-field activate-wide">
          <span>Company website</span>
          <input placeholder="Enter url" {...register("companyWebsite")} onBlur={event => setValue("companyWebsite", normalizeWebsite(event.target.value))} />
        </label>
        <label className="activate-field activate-wide">
          <span>About company*</span>
          <textarea
            maxLength={500}
            placeholder="Describe your company"
            {...aboutPromptRegister}
          />
          <em>{aboutPrompt.trim().length} / 500</em>
          {(errors.aboutPrompt || (!about && errors.about)) && (
            <small>{errors.aboutPrompt?.message || errors.about?.message}</small>
          )}
        </label>
        {about || showFinalDescription ? (
          <label className="activate-field activate-wide">
            <span>Company description*</span>
            <textarea
              maxLength={1000}
              placeholder={aboutLoader ? "Generating description..." : "Company description"}
              {...register("about", {
                required: "Description is required",
                minLength: { value: 30, message: "Description must be at least 30 characters" },
              })}
            />
            <em>{about.trim().length} / 1000</em>
            {errors.about && <small>{errors.about.message}</small>}
          </label>
        ) : (
          <input type="hidden" {...register("about", {
            required: "Description is required",
            minLength: { value: 30, message: "Description must be at least 30 characters" },
          })} />
        )}
        {about ? (
          <label className="activate-field activate-wide">
            <span>
              {amountFieldMeta.label}
              <button
                type="button"
                className="activate-inline-info"
                onClick={() => setInfoModalShow(true)}
                aria-label={`About ${amountFieldMeta.label}`}
              >
                i
              </button>
            </span>
            <input
              inputMode="numeric"
              placeholder="Enter amount"
              {...register("aumCapitalHeld", {
                onChange: event => {
                  event.target.value = event.target.value.replace(/[^0-9]/g, "");
                },
              })}
            />
          </label>
        ) : null}
        <div className="activate-upload activate-wide">
          <span>Upload new holdco logo</span>
          {logo ? (
            <div className="activate-logo-row">
              <img src={backendUrl + logo} alt="Company logo" />
              <button type="button" onClick={() => document.getElementById("activateLogoUpload")?.click()} disabled={logoUploading}>Replace</button>
              <button type="button" onClick={removeFile}>Remove</button>
            </div>
          ) : (
            <button type="button" onClick={() => document.getElementById("activateLogoUpload")?.click()} disabled={logoUploading}>
              {logoUploading ? "Uploading..." : "Click to upload"}
            </button>
          )}
          <input id="activateLogoUpload" type="file" accept="image/*" onChange={uploadFiles} hidden />
        </div>
      </div>
      <InfoModalStatic
        infoModalStaticShow={infoModalShow}
        infoModalStaticClose={() => setInfoModalShow(false)}
        infoModalStaicShowType={amountFieldMeta.popover}
      />
    </>
  );
}

function StaticStep({ selectedStep }) {
  const content = staticStepContent[selectedStep];

  return (
    <>
      <StepIntroCard step={selectedStep} />
      <div className="activate-static-grid">
        {content.items.map((item, index) => (
          <div className="activate-static-card" key={item}>
            <span>{index + 1}</span>
            <h3>{item}</h3>
            <p>This section is static for now and ready for the React Native logic when you share it.</p>
          </div>
        ))}
      </div>
    </>
  );
}

function ReviewPayStep({ businessData, companyId, onBack, onPaymentStepChange }) {
  const { selectedPlanProduct } = useSelector(state => state.subscriptionPlanProducts || {});
  const { stripeSubscription } = useSelector(state => state.stripe || {});
  const selectedPlanData = selectedPlanProduct?.id || selectedPlanProduct?._id ? selectedPlanProduct : stripeSubscription || {};
  const planSlug = selectedPlanData?.slug || selectedPlanData?.id || selectedPlanData?._id || "";
  const embeddedQuotationDetail = useMemo(() => {
    const currency = selectedPlanData?.currency?.value
      ? selectedPlanData.currency
      : { value: selectedPlanData?.currency || "usd" };

    return {
      ...selectedPlanData,
      id: selectedPlanData?.id || selectedPlanData?._id || planSlug,
      _id: selectedPlanData?._id || selectedPlanData?.id || planSlug,
      slug: planSlug,
      name: capitalizePlanName(selectedPlanData?.name),
      targetAudience: getPlanDescription(selectedPlanData),
      price: getPlanPrice(selectedPlanData),
      currency,
      attachment: selectedPlanData?.attachment || [],
      company: {
        companyName: businessData?.companyName || "kimbocorp",
        logo: businessData?.logo || "",
        isAdvisor: false,
        isDefault: false,
        _id: selectedPlanData?.company?._id || selectedPlanData?.company || "",
      },
      createdBy: selectedPlanData?.createdBy || {
        first_name: "Kimbo",
        last_name: "Corp",
      },
    };
  }, [businessData?.companyName, businessData?.logo, planSlug, selectedPlanData]);

  return (
    <div className="activate-reviewpay activate-reviewpay-embedded">
      <BankTransferPublicFlow
        embedded
        embeddedPlanId={planSlug}
        embeddedCompanyId={companyId}
        embeddedQuotationDetail={embeddedQuotationDetail}
        onEmbeddedBack={onBack}
        onEmbeddedStepChange={onPaymentStepChange}
      />
    </div>
  );
}

function BusinessClassificationStep({ businessData, stakeholderData, goalsData }) {
  const shareholders = stakeholderData.shareholders || [];
  const directors = stakeholderData.directors || [];
  const objectiveTitle = goalsData.statement?.title || "";
  const objectiveBody = goalsData.statement?.body || "";
  const shouldShowObjective = !goalsData?.isSkipped && (objectiveTitle || objectiveBody);

  return (
    <>
      <StepIntroCard step={4} />

      <div className="activate-classification-review">
        <section className="activate-review-section">
          <div className="activate-review-heading">
            <span>1</span>
            <div>
              <h3>Business Identity</h3>
              <p>Core business profile and activity information.</p>
            </div>
          </div>
          <div className="activate-review-grid">
            <ReviewField label="Business name" value={businessData.companyName || "Not provided"} />
            <ReviewField label="Primary activity" value={businessData.primaryActivity || "Not provided"} />
            <ReviewField label="Secondary activity" value={businessData.secondaryActivity || "Not provided"} />
            <ReviewField label="Website" value={getDisplayWebsite(businessData.companyWebsite) || "Not provided"} />
          </div>
          <div className="activate-review-note">
            <strong>Business description</strong>
            <p>{businessData.about || businessData.aboutPrompt || "No description has been added yet."}</p>
          </div>
        </section>

        <section className="activate-review-section">
          <div className="activate-review-heading">
            <span>2</span>
            <div>
              <h3>Shareholders & Directors</h3>
              <p>People added manually or imported from MyInfo.</p>
            </div>
          </div>
          <div className="activate-people-summary">
            <PeopleGroup title="Shareholders" source={stakeholderData.sources?.shareholders} entries={shareholders} />
            <PeopleGroup title="Directors" source={stakeholderData.sources?.directors} entries={directors} />
          </div>
        </section>

        {shouldShowObjective && (
          <section className="activate-review-section">
            <div className="activate-review-heading">
              <span>3</span>
              <div>
                <h3>Objective</h3>
              </div>
            </div>
            <div className="activate-review-note">
              <strong>{objectiveTitle}</strong>
              <p>{objectiveBody}</p>
            </div>
          </section>
        )}
      </div>
    </>
  );
}

function ReviewField({ label, value }) {
  return (
    <div className="activate-review-field">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function PeopleGroup({ title, source, entries }) {
  return (
    <div className="activate-people-group">
      <div>
        <strong>{title}</strong>
        <span>{source === "myinfo" ? "MyInfo import" : "Manual entry"}</span>
      </div>
      <p>{entries.length} {entries.length === 1 ? "person" : "people"} added</p>
      {entries.length ? (
        <div className="activate-people-list">
          {entries.slice(0, 4).map(entry => (
            <span key={entry.id}>{entry.name || entry.fullName}</span>
          ))}
          {entries.length > 4 && <span>+{entries.length - 4} more</span>}
        </div>
      ) : (
        <em>No {title.toLowerCase()} added yet.</em>
      )}
    </div>
  );
}

function ReviewChipBlock({ title, items = [], emptyText }) {
  return (
    <div className="activate-review-chip-block">
      <strong>{title}</strong>
      {items.length ? (
        <div className="activate-review-chips">
          {items.map(item => <span key={item}>{item}</span>)}
        </div>
      ) : (
        <p>{emptyText}</p>
      )}
    </div>
  );
}

export default function ActivateBusiness() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const { pendingCompanySelected, pendingCompany, recommendedPlan } = useSelector((state) => state.company);
  const { toastMessage } = useSelector(state => state.message);
  const user = getUniqueUser();
  const [selectedStep, setSelectedStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stepChanging, setStepChanging] = useState(false);
  const [paymentSubStep, setPaymentSubStep] = useState(null);
  const [descriptionGenerating, setDescriptionGenerating] = useState(false);
  const [campaignFlowSkipped, setCampaignFlowSkipped] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [tokenVerifying, setTokenVerifying] = useState(true);
  const [stepActionError, setStepActionError] = useState("");
  const [activationChatUserId, setActivationChatUserId] = useState(user?.userActivateAttribute || "");
  const [stakeholderSummary, setStakeholderSummary] = useState({
    shareholders: [],
    directors: [],
    sources: {
      shareholders: "manual",
      directors: "manual",
    },
  });
  const [goalsSummary, setGoalsSummary] = useState({
    goal: null,
    objectives: objectiveOptions.slice(0, 5),
    statement: {
      title: "Enhance Community Stakeholder Engagement",
      body: "We are keen to strengthen our community connections by engaging with key stakeholders and expanding our network. Through partnership development and local sourcing initiatives, we aim to foster relationships that support our mission of providing fresh, quality produce to our community.",
    },
    audience: {
      roles: ["Community Manager"],
      seniority: ["Senior Manager"],
      locations: ["Malaysia"],
      industries: ["Food Production"],
    },
  });
  const activationToken = searchParams.get("activationToken");
  const loginToken = searchParams.get("token");
  const requestedStep = parseInt(searchParams.get("step"), 10);
  const businessInfoChangeKeyRef = useRef("");
  const lastTrackedActivationStepRef = useRef(null);
  const paymentCompletedTrackedRef = useRef(false);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      companyName: "",
      companyWebsite: "",
      businessType: "",
      aumCapitalHeld: "",
      primaryActivity: "",
      secondaryActivity: "",
      aboutPrompt: "",
      about: "",
      logo: "",
      isTick: false,
    },
  });

  const formValues = watch();
  const handleStakeholderSummaryChange = useCallback(data => setStakeholderSummary(data), []);
  const handleGoalsSummaryChange = useCallback(data => {
    setCampaignFlowSkipped(false);
    setGoalsSummary(data);
  }, []);
  const cleanMyInfoCallbackUrl = useCallback(() => {
    const nextSearchParams = new URLSearchParams(location.search);
    const callbackKeys = ["step", "status", "result", "state", "myinfoId"];
    const hasCallbackParams = callbackKeys.some(key => nextSearchParams.has(key));

    if (!hasCallbackParams) return;

    callbackKeys.forEach(key => nextSearchParams.delete(key));
    const nextSearch = nextSearchParams.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}${location.hash}`, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!loginToken) return;

    localStorage.setItem("token", loginToken);

    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.delete("token");
    const nextSearch = nextSearchParams.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}${location.hash}`, { replace: true });
  }, [location.hash, location.pathname, location.search, loginToken, navigate]);

  useEffect(() => {
    if (!activationToken) {
      navigate(RoutesPaths.ERROR_PAGE, { replace: true });
      return;
    }

    dispatch(verifyUserUniqueTokenAction({ uniqueToken: activationToken })).then(resp => {
      if (resp.status && resp.isValid) {
        setActivationChatUserId(resp?.user?.userActivateAttribute || "");
        setTokenVerifying(false);
        dispatch(getAllCompany());
      } else {
        navigate(RoutesPaths.ERROR_PAGE, { replace: true });
      }
    });
  }, [activationToken, dispatch, navigate]);

  useEffect(() => {
    const pendingCompanies = Array.isArray(pendingCompany)
      ? pendingCompany
      : pendingCompany
        ? [pendingCompany]
        : [];
    const selected = id
      ? pendingCompanies.find(company => getCompanyId(company) === id)
      : getLatestPendingCompany(pendingCompanies);

    if (
      selected &&
      (
        getCompanyId(selected) !== getCompanyId(pendingCompanySelected) ||
        (!getCompanyId(selected) && selected !== pendingCompanySelected)
      )
    ) {
      dispatch(selectPendingCompany(selected));
    }
  }, [pendingCompany, pendingCompanySelected, dispatch, id]);

  useEffect(() => {
    if (!pendingCompanySelected) return;
    reset({
      companyName: pendingCompanySelected.companyName || "",
      companyWebsite: pendingCompanySelected.companyWebsite || "",
      businessType: pendingCompanySelected.businessType || "",
      aumCapitalHeld: pendingCompanySelected.aumCapitalHeld || "",
      primaryActivity: pendingCompanySelected.primaryActivity || "",
      secondaryActivity: pendingCompanySelected.secondaryActivity || "",
      aboutPrompt: pendingCompanySelected.aboutPrompt || pendingCompanySelected.about || "",
      about: pendingCompanySelected.about || "",
      logo: pendingCompanySelected.logo || "",
      isTick: pendingCompanySelected.isTick || false,
    });
  }, [pendingCompanySelected, reset]);

  useEffect(() => {
    dispatch(selectedCompanyStepAction(selectedStep));
    if (selectedStep !== 6) setPaymentSubStep(null);
    setStepActionError("");
    setStepChanging(false);
  }, [dispatch, selectedStep]);

  useEffect(() => {
    if (tokenVerifying) return;

    const analyticsStep = activationAnalyticsSteps[selectedStep];
    if (!analyticsStep) return;

    const companyId = pendingCompanySelected?.id || id || "";
    if (lastTrackedActivationStepRef.current === selectedStep) return;
    lastTrackedActivationStepRef.current = selectedStep;

    trackAnalyticsEvent(analyticsStep.eventName, {
      ...getActivationUserPayload(user),
      step_number: selectedStep,
      step_name: analyticsStep.label,
      company_id: companyId,
    });
  }, [id, pendingCompanySelected?.id, selectedStep, tokenVerifying, user]);

  useEffect(() => {
    const businessInfoChangeKey = [
      formValues.about,
      formValues.aboutPrompt,
      formValues.companyName,
      formValues.primaryActivity,
    ].join("|");

    if (!businessInfoChangeKeyRef.current) {
      businessInfoChangeKeyRef.current = businessInfoChangeKey;
      return;
    }

    if (selectedStep === 1 && stepActionError && businessInfoChangeKeyRef.current !== businessInfoChangeKey) {
      setStepActionError("");
    }

    businessInfoChangeKeyRef.current = businessInfoChangeKey;
  }, [
    formValues.about,
    formValues.aboutPrompt,
    formValues.companyName,
    formValues.primaryActivity,
    selectedStep,
    stepActionError,
  ]);

  useEffect(() => {
    if (tokenVerifying || Number.isNaN(requestedStep)) return;
    if (requestedStep >= 1 && requestedStep <= steps.length) {
      setSelectedStep(requestedStep);
    }
  }, [requestedStep, tokenVerifying]);

  useEffect(() => {
    if (selectedStep !== 6 || paymentSubStep !== 5) return;
    if (!paymentCompletedTrackedRef.current) {
      paymentCompletedTrackedRef.current = true;
      trackAnalyticsEvent("activation_payment_completed", {
        ...getActivationUserPayload(user),
        step_number: 7,
        step_name: "Payment completed (Activated)",
        company_id: pendingCompanySelected?.id || id || "",
      });
    }

    const nextSearchParams = new URLSearchParams(location.search);
    const hasPaymentStepParams =
      nextSearchParams.has("step") ||
      nextSearchParams.has("paymentStep");

    if (!hasPaymentStepParams) return;

    nextSearchParams.delete("step");
    nextSearchParams.delete("paymentStep");
    const nextSearch = nextSearchParams.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}${location.hash}`, { replace: true });
  }, [id, location.hash, location.pathname, location.search, navigate, paymentSubStep, pendingCompanySelected?.id, selectedStep, user]);

  const buildPayload = data => ({
    companyName: data?.companyName,
    companyWebsite: data?.companyWebsite || "",
    about: data?.about || "",
    aboutPrompt: data?.aboutPrompt || "",
    city: PREDEFINED_ADDRESS.CITY,
    country: PREDEFINED_ADDRESS.COUNTRY,
    postalCode: PREDEFINED_ADDRESS.POSTAL_CODE,
    primaryAddress: PREDEFINED_ADDRESS.LINE1,
    logo: data.logo || "",
    id: pendingCompanySelected ? pendingCompanySelected?.id : null,
    isTick: data.isTick || false,
    businessType: data?.businessType || "",
    aumCapitalHeld: data?.aumCapitalHeld || "",
    primaryActivity: data?.primaryActivity,
    secondaryActivity: data?.secondaryActivity || "",
  });

  const saveBusinessInfo = async data => {
    setStepActionError("");
    setSaving(true);
    try {
      const resp = await dispatch(addCompany(buildPayload(data), false));
      if (resp?.status) {
        await dispatch(getRecommendedPlanAction({
          primaryActivity: data?.primaryActivity,
          secondaryActivity: data?.secondaryActivity || "",
          aboutCompany: data?.aboutPrompt || "",
          companyDescription: data?.about || "",
        }, data?.aumCapitalHeld || 0));
        setSelectedStep(2);
        navigate(`${RoutesPaths.ACTIVATE_BUSINESS}?activationToken=${activationToken}&id=${resp?.data?.id}`, { replace: true });
      } else {
        setStepActionError(resp?.data || resp?.message || "Unable to save your business details. Please try again.");
        dispatch(clearMessage());
        setStepChanging(false);
      }
    } catch (error) {
      setStepActionError("Unable to save your business details. Please try again.");
      setStepChanging(false);
    } finally {
      setSaving(false);
    }
  };

  const handleBusinessInfoInvalid = () => {
    setStepActionError("Please complete the required fields above before continuing.");
    setStepChanging(false);
  };

  const handleContinue = () => {
    if (stepChanging) return;
    setStepActionError("");
    setStepChanging(true);
    if (selectedStep === 1) {
      handleSubmit(saveBusinessInfo, handleBusinessInfoInvalid)();
      return;
    }
    if (selectedStep === 2) cleanMyInfoCallbackUrl();
    setSelectedStep(step => Math.min(step + 1, steps.length));
  };

  const handleBack = () => {
    if (stepChanging) return;
    setStepChanging(true);
    if (selectedStep === 1) {
      navigate(
        activationChatUserId
          ? RoutesPaths.MESSAGE_DETAIL_PUBLIC_CHAT.replace(":id", activationChatUserId)
          : RoutesPaths.MESSAGE_DETAIL_PUBLIC,
        { replace: true }
      );
      return;
    }
    if (selectedStep === 2) cleanMyInfoCallbackUrl();
    setSelectedStep(step => Math.max(step - 1, 1));
  };

  const goToStep = step => {
    if (stepChanging) return;
    setStepChanging(true);
    if (selectedStep === 2 && step !== 2) cleanMyInfoCallbackUrl();
    setSelectedStep(step);
  };

  const uploadFiles = event => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    setLogoUploading(true);
    dispatch(singleFileUploadAction(formData))
      .then(resp => {
        if (resp) setValue("logo", resp?.filename);
        setLogoUploading(false);
      })
      .catch(() => setLogoUploading(false));
    event.target.value = null;
  };

  const removeFile = () => setValue("logo", "");

  const showRecommendedSummary = selectedStep >= 2 && selectedStep !== 5 && !(selectedStep === 6 && paymentSubStep === 5);
  const showRightSummary = showRecommendedSummary;

  if (tokenVerifying) {
    return (
      <div className="activate-page">
        <div className="activate-token-loader">Verifying activation link...</div>
      </div>
    );
  }

  return (
    <div className="activate-page">
      <StepRail selectedStep={selectedStep} />
      <main className="activate-main">
        <ActivationHeader user={user} />
        {toastMessage && !stepActionError && <GetToaster />}
        <div className={`activate-content ${showRightSummary ? "" : "activate-content-full"}`}>
          <section className="activate-work">
            {selectedStep === 1 && (
              <BusinessInfoStep
                register={register}
                errors={errors}
                watch={watch}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                getValues={getValues}
                recommendedPlan={recommendedPlan}
                logoUploading={logoUploading}
                uploadFiles={uploadFiles}
                removeFile={removeFile}
                onGeneratingChange={setDescriptionGenerating}
              />
            )}
            {selectedStep === 2 && (
              <ShareholdersDirectorsStep
                companyId={pendingCompanySelected?.id || id}
                onBack={handleBack}
                onContinue={() => goToStep(3)}
                onDataChange={handleStakeholderSummaryChange}
                navigationDisabled={stepChanging}
              />
            )}
            {selectedStep === 3 && (
              <GoalsObjectivesStep
                company={pendingCompanySelected}
                companyName={formValues.companyName}
                onBack={handleBack}
                onContinue={() => goToStep(4)}
                onSkip={() => {
                  setCampaignFlowSkipped(true);
                  setGoalsSummary(prev => ({
                    ...prev,
                    isSkipped: true,
                    goal: null,
                    objectives: [],
                    statement: { title: "", body: "" },
                    audience: {
                      roles: [],
                      seniority: [],
                      locations: [],
                      industries: [],
                    },
                  }));
                  goToStep(4);
                }}
                onDataChange={handleGoalsSummaryChange}
                navigationDisabled={stepChanging}
              />
            )}
            {selectedStep === 4 && (
              <BusinessClassificationStep
                businessData={formValues}
                stakeholderData={stakeholderSummary}
                goalsData={{ ...goalsSummary, isSkipped: campaignFlowSkipped || goalsSummary.isSkipped }}
              />
            )}
            {selectedStep === 5 && (
              <PlansStep
                companyId={pendingCompanySelected?.id || id}
                onBack={handleBack}
                onContinue={() => goToStep(6)}
                selectedPlan={selectedPlan}
                setSelectedPlan={setSelectedPlan}
                navigationDisabled={stepChanging}
              />
            )}
            {selectedStep === 6 && (
              <ReviewPayStep
                businessData={formValues}
                companyId={pendingCompanySelected?.id || id}
                onBack={handleBack}
                onPaymentStepChange={setPaymentSubStep}
              />
            )}
            {selectedStep > 4 && selectedStep !== 5 && selectedStep !== 6 && <StaticStep selectedStep={selectedStep} />}
            {selectedStep !== 2 && selectedStep !== 3 && selectedStep !== 5 && selectedStep !== 6 && (
              <div className={`activate-actions${stepActionError ? " has-error" : ""}`}>
                {stepActionError && (
                  <p className="activate-actions-error" role="alert">
                    {stepActionError}
                  </p>
                )}
                <button type="button" className="activate-back" onClick={handleBack} disabled={stepChanging || saving}>
                  Back
                </button>
                <button
                  type="button"
                  className="activate-continue"
                  onClick={handleContinue}
                  disabled={stepChanging || saving || (selectedStep === 1 && descriptionGenerating)}
                >
                  {saving ? "Saving..." : selectedStep === 1 && descriptionGenerating ? "Generating..." : selectedStep === steps.length ? "Finish" : "Continue"}
                </button>
              </div>
            )}
          </section>
          {showRightSummary && (
            <aside className="activate-summary">
              <RecommendedPlanCard recommendedPlan={recommendedPlan} variant="summary" />
            </aside>
          )}
        </div>
      </main>
    </div>
  );
}
